// src/components/DashboardGroups/DashboardGroups.tsx
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { useInfoWindow } from '@/contexts/InfoWindowContext';
import { conversationService } from '@/services/conversationService';
import conversationApi from '@/services/conversationApi';
import './DashboardGroups.css';
import { arrayMove } from '@dnd-kit/sortable';
import { DashboardGroupColumn } from './DashboardGroupColumn';
import { dashboardGroupsService } from './service';
import { dashboardGroupService } from '@/services/dashboardGroupService';
import { dashboardService } from '@/services/dashboardService';
import { authStorage } from '@/lib/storage';
import { readViewCache, writeViewCache, removeViewCache } from '@/lib/viewCache';
import type {
  DashboardGroupsProps,
  DashboardGroupData,
  DashboardGroupsRequestParams,
} from './types';
import type { Conversation, ConversationDetail } from '@/types/conversation';
import type { ApiConfig, AppConfig } from '@/components/ConversationItem/types';
import EditCard from '@/components/EditCard/EditCard';
import { ConfirmDialog } from '@/components/ui/Dialog';
import { config as envConfig } from '@/config/env';
import { getBookmarksConfigValueId } from '@/components/utils/CorgeeHelper';
import type { DuplicateCardRef, EditCardRef } from '@/components/EditCard/EditCard';
import DuplicateCard from '../DuplicateCard/DuplicateCard';
import type { NestableItem } from './treeUtils';
import { flattenChildTree, moveConversationsToTop } from './treeUtils';
import { getDashboardCachePrefix } from './dashboardCache';
import CorgeeStorageHelper from '../Globals/CorgeeStorageHelper';
import { setSelectedCardTitles, getSelectedCardTitles, getCardBeingDragged } from '@/lib/cardDragState';
import type { Dashboard } from '../TabControl';
import type { SideMenuItem } from '@/services/menuService';
import { useOutletContext } from 'react-router-dom';
import { conversationBus } from '@/lib/conversationBus';

/** Cards cached per column: enough to paint the first screen. */
const CACHE_CARDS_PER_COLUMN = 25;

const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Debounce for cache writes: drag-over rewrites groups on every frame. */
const CACHE_PERSIST_DELAY_MS = 500;

// Backend responses can repeat a ConversationId (e.g. a conversation matching multiple joined layers/filters).
const dedupeByConversationId = <T extends { ConversationId: number }>(list: T[]): T[] =>
{
  const seenIds = new Set<number>();
  return list.filter(c =>
  {
    if (seenIds.has(c.ConversationId)) return false;
    seenIds.add(c.ConversationId);
    return true;
  });
};

// Helper function to get dynamic API configuration from session
const getApiConfig = (): ApiConfig & {
  refreshToken: string;
  ExternalData: string;
  SocketServer: string;
  TenancyName: string;
  PersonId: string;
} =>
{
  const token = authStorage.getAccessToken() || '';
  const personId = authStorage.getPersonId() || '0';

  return {
    url: envConfig.api.baseUrl,
    token: token,
    refreshToken: '',
    ExternalData: '',
    SocketServer: envConfig.socket.server,
    TenancyName: envConfig.tenant.defaultName,
    PersonId: personId,
  };
};

// Helper function to get dynamic App configuration
const getAppConfig = (): AppConfig & {
  buildVersion?: string;
  webChaturl?: string;
  appPrefix?: string;
} =>
{
  const webUrl = import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/';
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost/Corgee.Web/';
  return {
    weburl: webUrl,
    apiurl: apiUrl,
    buildVersion: '1.0.0',
    webChaturl: webUrl + '/CHAT',
    appPrefix: '',
  };
};

// Storage Data Configuration - reads from real session/localStorage
const getStorageData = () =>
{
  let data: Record<string, any> = {};
  try
  {
    data = JSON.parse(sessionStorage.getItem('C2_data') || '{}');
  }
  catch
  {
    /* ignore */
  }

  let allLayers: any[] = [];
  try
  {
    allLayers = JSON.parse(sessionStorage.getItem('C2_AllLayers') || '[]');
  }
  catch
  {
    /* ignore */
  }

  let layersToHide: any[] = [];
  try
  {
    layersToHide = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
  }
  catch
  {
    /* ignore */
  }

  return {
    data,
    allLayers,
    layersToHide,
    layerId: parseInt(localStorage.getItem('C2_layerid') || '0'),
    conv_maxAllowedUpload: 20480000,
    enableChat: localStorage.getItem('C2_enableChat') === 'true',
    personid: parseInt(localStorage.getItem('C2_personid') || '0'),
  };
};

// Menu Configuration
const getConfigMenus = () => ({
  conversation: [
    'goto-project',
    'close-infowindow',
    'opendetails-newtab',
    'mark-redflag',
    'mark-important',
    'follow-conv',
    'create-itemtype',
    'convert-to',
    'duplicate-project',
    'remove-from-column',
    'copylink-to-clipboard',
    'move-to',
    'copy-to',
    'submit-time',
    'edit-history',
    'edit',
    'delete',
    'duplicate-item',
    'archive',
    'edit-security-groups',
    'toggle-user-status',
  ],
  reply: ['mark-favorite', 'repost', 'edit', 'delete', 'create-itemtype', 'mark-important'],
});

// Real Item Types Configuration
const realItemTypes = {
  Users: {
    Name: 'People',
    Icon: 'icons-menu-account-box',
  },
  Tasks: {
    Name: 'Tasks',
    Icon: 'icons-menu-epic',
  },
};

// User Data
const getUserData = () => ({
  imageProfile: 'Images/user.png?1.0.21.8',
  personName: 'Test User',
});

export const DashboardGroups: React.FC<DashboardGroupsProps> = ({
  dashboardId,
  projectId = '',
  projectName = '',
  filters = '[]',
  searchText = '',
  layersToHide: layersToHideProp,
  pageSize = 130,
  dashboardViewTypeId = 1,
  isFilterPersonal = false,
  configValueId,
  extraFilterParams = {},
  initialData,
  recycleBin = false,
  cardViewMode = 'compact',
  columnWidth = 350,
  columnMinWidth: _columnMinWidth = 270,
  columnMaxWidth: _columnMaxWidth = 500,
  resizableColumns: _resizableColumns = false,
  onDataLoaded,
  onError,
  onCardMoved,
  onOpenInfoWindow,
  hideProjectName,
  onRegisterRefresh,
  onLoadMoreCards,
  pendingGroupIds,
  pendingConversationIds,
  reloadTrigger,
}) =>
{
  const socketConnectionId = useSocketConnectionId();
  const { registerUpdateCustomFields } = useInfoWindow();
  const [groups, setGroups] = useState<DashboardGroupData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isBackgroundRefresh, setIsBackgroundRefresh] = useState(false);
  const [activeCardId, setActiveCardId] = useState<number | null>(null);
  const [activeColumnId, _setActiveColumnId] = useState<number | null>(null);
  // Ref to remember which column a card was dragged FROM (active.data.current is reactive
  // and gets updated by handleDragOver before handleDragEnd reads it)
  const dragSourceColumnRef = React.useRef<number | null>(null);
  void dragSourceColumnRef;
  const [selectedCardIds, setSelectedCardIds] = useState<Set<number>>(new Set());
  const selectedCardIdsRef = useRef(selectedCardIds);
  selectedCardIdsRef.current = selectedCardIds;
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const dashboardIdRef = useRef(dashboardId);
  dashboardIdRef.current = dashboardId;
  const [columnWidths, setColumnWidths] = useState<Record<number, number>>({});
  const [columnTotals, setColumnTotals] = useState<Record<number, number>>({});
  const [sessionLayersToHide, setSessionLayersToHide] = useState<string>(() =>
  {
    try
    {
      const arr = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
      return arr.length ? arr.join(',') : '';
    }
    catch
    {
      return '';
    }
  });
  // When the parent passes layersToHideProp (even as ''), it overrides sessionStorage layers.
  // undefined means "use sessionStorage as usual".
  const effectiveLayersToHide = layersToHideProp !== undefined ? layersToHideProp : sessionLayersToHide;
  const [addingTaskGroupId, setAddingTaskGroupId] = useState<number | null>(null);
  const [pendingDeleteConversationId, setPendingDeleteConversationId] = useState<number | null>(
    null,
  );
  const [pendingDeleteGroupId, setPendingDeleteGroupId] = useState<number>(0);

  // Hierarchy: track which parent cards are expanded
  const [expandedParentIds, setExpandedParentIds] = useState<Set<number>>(new Set());
  // Guard against concurrent fetches for the same conversationId
  const expandingInFlight = useRef<Set<number>>(new Set());

  // Track whether any child-zone droppable is being hovered (suppresses normal placeholder)
  const [hierarchyDropActive, _setHierarchyDropActive] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fakeScrollRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);

  // Ref to reliably track the hierarchy target (survives rapid collision detection oscillation)
  const hierarchyTargetRef = useRef<{ parentCardId: number; groupId: number } | null>(null);
  void hierarchyTargetRef;

  // Per-column infinite scroll pagination
  const columnPagesRef = React.useRef<Record<number, number>>({});
  const columnHasMoreRef = React.useRef<Record<number, boolean>>({});
  const columnLoadingMoreRef = React.useRef<Record<number, boolean>>({});
  const [columnHasMore, setColumnHasMore] = useState<Record<number, boolean>>({});
  const [columnLoadingMore, setColumnLoadingMore] = useState<Record<number, boolean>>({});

  const { registerLoadData } = useOutletContext<{ registerLoadData: (fn: any) => void }>();

  // Sync fake scrollbar position after scrollWidth is updated (inner div has correct width).
  useLayoutEffect(() =>
  {
    if (scrollWidth === 0) return;
    const container = scrollContainerRef.current;
    const fakeScroll = fakeScrollRef.current;
    if (!container || !fakeScroll) return;
    if (fakeScroll.scrollLeft !== container.scrollLeft)
    {
      fakeScroll.scrollLeft = container.scrollLeft;
    }
  }, [scrollWidth]);

  // Auto-scroll horizontally when dragging a card/task near the left/right edges of the scroll container
  useEffect(() =>
  {
    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollInterval: ReturnType<typeof setInterval> | null = null;
    let lastClientX = 0;

    const stopScrolling = () =>
    {
      if (scrollInterval)
      {
        clearInterval(scrollInterval);
        scrollInterval = null;
      }
    };

    const handleDragOver = (e: DragEvent) =>
    {
      // Only auto-scroll when a card/task is actively being dragged
      if (!getCardBeingDragged())
      {
        stopScrolling();
        return;
      }

      lastClientX = e.clientX;

      const rect = container.getBoundingClientRect();
      const leftBoundary = rect.left + 150;  // 150px scroll zone on the left
      const rightBoundary = rect.right - 150; // 150px scroll zone on the right

      const inLeftZone = lastClientX < leftBoundary && lastClientX > rect.left;
      const inRightZone = lastClientX > rightBoundary && lastClientX < rect.right;

      if (inLeftZone || inRightZone)
      {
        if (!scrollInterval)
        {
          scrollInterval = setInterval(() =>
          {
            if (!container || !getCardBeingDragged())
            {
              stopScrolling();
              return;
            }

            const currentRect = container.getBoundingClientRect();
            const currentLeftBoundary = currentRect.left + 150;
            const currentRightBoundary = currentRect.right - 150;

            if (lastClientX < currentLeftBoundary && lastClientX > currentRect.left)
            {
              // Proximity-based speed: scroll faster when closer to the edge
              const distance = currentLeftBoundary - lastClientX;
              const ratio = Math.min(1, Math.max(0, distance / 100));
              const step = Math.round(2 + ratio * 20); // 2px to 22px
              container.scrollLeft -= step;
            }
            else if (lastClientX > currentRightBoundary && lastClientX < currentRect.right)
            {
              // Proximity-based speed: scroll faster when closer to the edge
              const distance = lastClientX - currentRightBoundary;
              const ratio = Math.min(1, Math.max(0, distance / 100));
              const step = Math.round(2 + ratio * 20); // 2px to 22px
              container.scrollLeft += step;
            }
            else
            {
              stopScrolling();
            }
          }, 20); // 50 FPS for smooth horizontal scrolling
        }
      }
      else
      {
        stopScrolling();
      }
    };

    const handleDragEndOrDrop = () =>
    {
      stopScrolling();
    };

    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragend', handleDragEndOrDrop);
    window.addEventListener('drop', handleDragEndOrDrop);
    window.addEventListener('corgee:card-drag-end', handleDragEndOrDrop);

    return () =>
    {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragend', handleDragEndOrDrop);
      window.removeEventListener('drop', handleDragEndOrDrop);
      window.removeEventListener('corgee:card-drag-end', handleDragEndOrDrop);
      stopScrolling();
    };
  }, [loading, groups.length]);

  // Sync horizontal scroll positions and monitor content width/position changes for floating horizontal scrollbar
  useEffect(() =>
  {
    const container = scrollContainerRef.current;
    const fakeScroll = fakeScrollRef.current;
    if (!container || !fakeScroll) return;

    let isSyncingContainer = false;
    let isSyncingFake = false;

    const handleContainerScroll = () =>
    {
      if (isSyncingContainer)
      {
        isSyncingContainer = false;
        return;
      }
      isSyncingFake = true;
      fakeScroll.scrollLeft = container.scrollLeft;
    };

    const handleFakeScroll = () =>
    {
      if (isSyncingFake)
      {
        isSyncingFake = false;
        return;
      }
      isSyncingContainer = true;
      container.scrollLeft = fakeScroll.scrollLeft;
    };

    container.addEventListener('scroll', handleContainerScroll, { passive: true });
    fakeScroll.addEventListener('scroll', handleFakeScroll, { passive: true });

    // Function to calculate and update layout styles for fixed scrollbar
    const updateLayout = () =>
    {
      const rect = container.getBoundingClientRect();
      const hasScroll = container.scrollWidth > container.clientWidth;

      // Update fake scrollbar position on screen
      fakeScroll.style.left = `${rect.left}px`;
      fakeScroll.style.width = `${rect.width}px`;
      fakeScroll.style.display = hasScroll ? 'block' : 'none';

      // Update the inner scrollable content width
      setScrollWidth(container.scrollWidth);
    };

    // Defer the first measurement by one rAF so the browser has finished
    // laying out all columns before we check scrollWidth vs clientWidth.
    // Without this, updateLayout() can run before columns have their full
    // painted width and incorrectly hides the scrollbar on first load.
    let rafId = requestAnimationFrame(updateLayout);

    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(container);
    Array.from(container.children).forEach(child => resizeObserver.observe(child));

    // MutationObserver: re-run updateLayout whenever columns or their content
    // are added/removed (covers dashboard switches and first-load card rendering).
    const mutationObserver = new MutationObserver(() =>
    {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateLayout);
    });
    mutationObserver.observe(container, { childList: true, subtree: true });

    // Listen to window events (resize, scroll) to update position if container shifts
    window.addEventListener('resize', updateLayout, { passive: true });
    window.addEventListener('scroll', updateLayout, { capture: true, passive: true });

    return () =>
    {
      cancelAnimationFrame(rafId);
      container.removeEventListener('scroll', handleContainerScroll);
      fakeScroll.removeEventListener('scroll', handleFakeScroll);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout);
    };
  }, [groups, loading]);

  // Keep refs in sync with state for use inside callbacks
  columnHasMoreRef.current = columnHasMore;
  columnLoadingMoreRef.current = columnLoadingMore;

  // Ref to prevent double-fetching in React StrictMode (stores last fetched params key)
  const hasFetchedRef = React.useRef<string | null>(null);

  // EditCard Refs
  const editCardRef = useRef<EditCardRef>(null);
  const editCardInitRef = useRef<((conv: any, itemType: any) => void) | null>(null);
  const createSourceConvRef = useRef<Conversation | null>(null);

  // DuplicateCard Refs
  const duplicateCardRef = useRef<DuplicateCardRef>(null);
  const duplicateCardInitRef = useRef<((conv: any) => void) | null>(null);

  // Configuration state
  const [api] = useState(() => getApiConfig());
  const [config] = useState(() => getAppConfig());

  // DnD sensors removed — react-dnd uses HTML5Backend which handles this natively

  // Use refs for callbacks to avoid re-triggering effects
  const onDataLoadedRef = React.useRef(onDataLoaded);
  const onErrorRef = React.useRef(onError);

  // Keep refs updated
  React.useEffect(() =>
  {
    onDataLoadedRef.current = onDataLoaded;
    onErrorRef.current = onError;
  }, [onDataLoaded, onError]);

  // When initialData is provided, load it directly and skip internal fetch
  React.useEffect(() =>
  {
    if (!initialData) return;
    const lower = (searchText || '').toLowerCase().trim();
    const filtered = lower
      ? initialData.map(group => ({
        ...group,
        Conversations: group.Conversations.filter(
          c =>
            (c.Title || '').toLowerCase().includes(lower) ||
              ((c as any).ProjectName || '').toLowerCase().includes(lower),
        ),
      }))
      : initialData;
    setGroups(filtered);
    setLoading(false);

    // Initialize columnHasMore for initialData mode
    const hasMoreMap: Record<number, boolean> = {};
    filtered.forEach(group =>
    {
      const loadedItems = group.Conversations?.length || 0;
      const totalItems = group.TotalItems;

      // If we have total info, use it; otherwise assume more if we loaded many items
      if (totalItems !== undefined)
      {
        hasMoreMap[group.DashboardGroupId] = loadedItems < totalItems;
      }
      else
      {
        hasMoreMap[group.DashboardGroupId] = loadedItems >= 50;
      }
    });
    setColumnHasMore(hasMoreMap);
  }, [initialData, searchText]);

  // Escuchar cambios en layers ocultos
  useEffect(() =>
  {
    const handleLayersChanged = () =>
    {
      try
      {
        const arr = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
        setSessionLayersToHide(arr.length ? arr.join(',') : '');
      }
      catch
      {
        setSessionLayersToHide('');
      }
    };
    window.addEventListener('layersToHideChanged', handleLayersChanged);
    return () => window.removeEventListener('layersToHideChanged', handleLayersChanged);
  }, []);

  // Cache Helper Functions
  const getCacheKey = useCallback(() =>
  {
    // Creating a stable key based on current view parameters
    const safeFilters = filters || '[]';
    const safeSearch = searchText || '';
    const safeProject = projectId || '';
    const safeLayer = effectiveLayersToHide || '';
    const safeExtra = JSON.stringify(extraFilterParams || {});

    return `${getDashboardCachePrefix(dashboardId)}${safeProject}_${configValueId}_${dashboardViewTypeId}_${btoa(safeFilters).substring(0, 20)}_${safeSearch}_${safeLayer}_${btoa(safeExtra).substring(0, 20)}`;
  }, [
    dashboardId,
    projectId,
    configValueId,
    dashboardViewTypeId,
    filters,
    searchText,
    effectiveLayersToHide,
    extraFilterParams,
  ]);

  const saveToCache = useCallback((key: string, data: DashboardGroupData[]) =>
  {
    const trimmed = data.map(group => ({
      ...group,
      TotalItems: group.TotalItems ?? group.Conversations?.length ?? 0,
      Conversations: (group.Conversations || []).slice(0, CACHE_CARDS_PER_COLUMN),
    }));

    writeViewCache(key, trimmed);
  }, []);

  const loadFromCache = useCallback(
    (key: string): DashboardGroupData[] | null =>
      readViewCache<DashboardGroupData[]>(key, CACHE_MAX_AGE_MS),
    [],
  );

  /** Cache key the current groups belong to; the key changes before the data does. */
  const groupsCacheKeyRef = useRef<string | null>(null);
  const pendingSnapshotRef = useRef<{ key: string; groups: DashboardGroupData[] } | null>(null);

  // Persist groups on every settled mutation, so handlers don't each cache
  useEffect(() =>
  {
    if (groups.length === 0) return;

    const key = getCacheKey();

    // View changed before the debounce fired: flush under its own key
    const pending = pendingSnapshotRef.current;
    if (pending && pending.key !== key)
    {
      saveToCache(pending.key, pending.groups);
      pendingSnapshotRef.current = null;
    }

    if (groupsCacheKeyRef.current !== key) return;

    pendingSnapshotRef.current = { key, groups };
    const timer = setTimeout(() =>
    {
      saveToCache(key, groups);
      pendingSnapshotRef.current = null;
    }, CACHE_PERSIST_DELAY_MS);

    return () => clearTimeout(timer);
  }, [groups, getCacheKey, saveToCache]);

  // Flush on unmount: navigating away would cancel the pending timer
  useEffect(
    () => () =>
    {
      const pending = pendingSnapshotRef.current;
      if (pending) saveToCache(pending.key, pending.groups);
    },
    [saveToCache],
  );

  // Load data - only depends on API-relevant params
  const loadData = useCallback(
    async (background = false, moveToTopIds?: number[]) =>
    {
      const cacheKey = getCacheKey();
      const moveToTopSet = new Set(moveToTopIds ?? []);

      try
      {
        if (background)
        {
          setIsBackgroundRefresh(true);
        }
        else
        {
          // Try Cache First
          const cachedData = loadFromCache(cacheKey);
          if (cachedData && cachedData.length > 0)
          {
            groupsCacheKeyRef.current = cacheKey;
            setGroups(cachedData);
            setLoading(false); // Skip skeleton if we have cache
            setIsBackgroundRefresh(true); // Treat API call as background update
          }
          else
          {
            setLoading(true);
            conversationBus.emit('dashboard-groups-loading', { isLoading: true });
          }
        }

        // ConfigValueId logic:
        // - Dashboards (1): don't send
        // - Bookmarks: send 0
        // - Others: send actual configValueId
        const bookmarksId = getBookmarksConfigValueId();
        const isBookmarks = configValueId === bookmarksId;
        const shouldSendConfigValueId = configValueId && configValueId !== 1;
        const resolvedConfigValueId = isBookmarks ? 0 : configValueId;

        const params: DashboardGroupsRequestParams = {
          CustomFields: extraFilterParams?.CustomFields || [],
          PageNumber: 1,
          PageSize: pageSize,
          DashboardId: dashboardId,
          Project: projectId,
          Filters: filters,
          SearchText: searchText,
          LayersToHide: effectiveLayersToHide,
          DashboardViewTypeId: dashboardViewTypeId,
          IsFilterPersonal: isFilterPersonal,
          ...(shouldSendConfigValueId && { ConfigValueId: resolvedConfigValueId }),
          ...extraFilterParams,
        };

        let response = await dashboardGroupsService.getInitialDashboardGroupConversations(params);

        response = response.map(x =>
        {
          x.Conversations.map(x =>
          {
            x.DashboardId = dashboardId;
            return x;
          });
          // Cards changed by other users (socket refresh) go first in their column
          x.Conversations = moveConversationsToTop(dedupeByConversationId(x.Conversations), moveToTopSet);
          return x;
        });
        groupsCacheKeyRef.current = cacheKey;
        setGroups(response);

        // Reset per-column pagination state on fresh load
        columnPagesRef.current = {};
        setColumnLoadingMore({});

        // Fetch total items per column from API
        try
        {
          const totals = await dashboardService.getTotalItemsByColumn({
            CustomFields: extraFilterParams?.CustomFields || [],
            PageNumber: 1,
            PageSize: pageSize,
            DashboardId: dashboardId,
            Project: projectId || null,
            Filters: filters,
            SearchText: searchText || null,
            LayersToHide: effectiveLayersToHide || null,
            DashboardViewTypeId: dashboardViewTypeId,
            IsFilterPersonal: isFilterPersonal,
            ...(shouldSendConfigValueId && { ConfigValueId: resolvedConfigValueId }),
            ...extraFilterParams,
          });
          const totalsMap = totals.reduce(
            (acc, item) =>
            {
              acc[item.DashboardGroupId] = item.TotalItems;
              return acc;
            },
            {} as Record<number, number>,
          );

          // If the API returned children in the initial page (ParentConversationId set),
          // add them to the totals since getTotalItemsByColumn only counts root cards.
          response.forEach(group =>
          {
            const childCount = group.Conversations.filter(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              c => (c as any).ParentConversationId && (c as any).ParentConversationId !== 0,
            ).length;
            if (childCount > 0 && totalsMap[group.DashboardGroupId] !== undefined)
            {
              totalsMap[group.DashboardGroupId] = (totalsMap[group.DashboardGroupId] ?? 0) + childCount;
            }
          });

          setColumnTotals(totalsMap);

          // Initialize columnHasMore based on whether each column has more items to load
          const hasMoreMap: Record<number, boolean> = {};
          response.forEach(group =>
          {
            const totalItems = totalsMap[group.DashboardGroupId];
            const loadedItems = group.Conversations?.length || 0;

            // If we have total info, use it; otherwise assume more if we loaded near a full page
            if (totalItems !== undefined)
            {
              hasMoreMap[group.DashboardGroupId] = loadedItems < totalItems;
            }
            else
            {
              // If no total info, assume more items if we loaded >= 50 items (likely means there's pagination)
              hasMoreMap[group.DashboardGroupId] = loadedItems >= 50;
            }
          });
          setColumnHasMore(hasMoreMap);
        }
        catch (totalError)
        {
          console.warn('Could not fetch column totals:', totalError);
          // Fallback: assume columns with many items might have more
          const fallbackMap: Record<number, boolean> = {};
          response.forEach(group =>
          {
            const loadedItems = group.Conversations?.length || 0;
            fallbackMap[group.DashboardGroupId] = loadedItems >= 50;
          });
          setColumnHasMore(fallbackMap);
        }

        // Call callback via ref (doesn't trigger re-fetch)
        if (onDataLoadedRef.current)
        {
          onDataLoadedRef.current(response);
        }
      }
      catch (error)
      {
        console.error('Error loading dashboard groups:', error);
        // Call callback via ref (doesn't trigger re-fetch)
        if (onErrorRef.current)
        {
          onErrorRef.current(error as Error);
        }
      }
      finally
      {
        setLoading(false);
        setIsBackgroundRefresh(false);
        conversationBus.emit('dashboard-groups-loading', { isLoading: false });
      }
    },
    [
      dashboardId,
      projectId,
      filters,
      searchText,
      effectiveLayersToHide,
      pageSize,
      dashboardViewTypeId,
      isFilterPersonal,
      configValueId,
      extraFilterParams,
      getCacheKey,
      loadFromCache,
    ],
  );

  // Kept in sync on every render (not via a useEffect dependency) so any consumer
  // holding a reference to this ref always sees the current dashboard's loadData —
  // loadData's own identity changes whenever dashboardId/filters/etc. change.
  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;

  useEffect(() =>
  {
    // Register a stable wrapper, not `loadData` itself. Registering `loadData`
    // directly here would freeze this dashboard's initial dashboardId/filters into
    // MainLayout's childFunctionRef forever (empty deps → runs once, at mount) —
    // so a later call to functionsLoad() from InfoWindow (or anywhere else) could
    // reload a stale/wrong dashboard instead of whichever one is actually visible,
    // depending on tab-switch history. The wrapper always delegates to whatever
    // loadData currently is via loadDataRef.
    registerLoadData((...args: Parameters<typeof loadData>) => loadDataRef.current(...args));
  }, []);

  // Initialize/update column widths when groups or columnWidth change
  useEffect(() =>
  {
    if (groups.length > 0)
    {
      const initialWidths: Record<number, number> = {};
      groups.forEach(group =>
      {
        initialWidths[group.DashboardGroupId] = columnWidth;
      });
      setColumnWidths(initialWidths);
    }
  }, [groups, columnWidth]);

  // Track previous viewTypeId to detect 1→2 transitions
  const prevViewTypeIdRef = React.useRef<number>(dashboardViewTypeId);

  useEffect(() =>
  {
    // Skip internal fetch when caller provides data directly
    if (initialData) return;

    // Prevent double-fetching in React StrictMode by checking if we've already fetched with same params
    const extraKey = JSON.stringify(extraFilterParams);
    const baseKey = `${dashboardId}-${projectId}-${filters}-${searchText}-${configValueId}-${effectiveLayersToHide}-${extraKey}`;
    const paramsKey = `${baseKey}-${dashboardViewTypeId}`;
    if (hasFetchedRef.current === paramsKey) return;

    const hadData = hasFetchedRef.current !== null;
    const prevBaseKey = hasFetchedRef.current?.replace(/-[^-]*$/, '');
    const onlyViewTypeChanged = hadData && prevBaseKey === baseKey;
    const wasCompact = prevViewTypeIdRef.current === 1;
    const isNowLarge = dashboardViewTypeId === 2;

    prevViewTypeIdRef.current = dashboardViewTypeId;
    hasFetchedRef.current = paramsKey;

    // Only background refresh when switching from compact (1) to large (2)
    if (onlyViewTypeChanged && wasCompact && isNowLarge)
    {
      loadData(true);
    }
    else if (onlyViewTypeChanged)
    {
      // 2→1: no API call needed, just visual change
      return;
    }
    else
    {
      loadData(false);
    }
  }, [
    loadData,
    dashboardId,
    projectId,
    filters,
    searchText,
    configValueId,
    effectiveLayersToHide,
    dashboardViewTypeId,
    extraFilterParams,
  ]);

  // Force reload when reloadTrigger increments (e.g. user clicks the already-active tab)
  const prevReloadTriggerRef = React.useRef(reloadTrigger);
  React.useEffect(() =>
  {
    if (reloadTrigger === undefined) return;
    if (prevReloadTriggerRef.current === reloadTrigger) return;
    prevReloadTriggerRef.current = reloadTrigger;
    hasFetchedRef.current = null;
    setExpandedParentIds(new Set());
    expandingInFlight.current.clear();
    removeViewCache(getCacheKey());
    loadData(false);
  }, [reloadTrigger, loadData, getCacheKey]);

  // Register a background-refresh function with the parent page so it can trigger
  // a reload in response to socket notifications without accessing internal state.
  const onRegisterRefreshRef = React.useRef(onRegisterRefresh);
  React.useEffect(() =>
  {
    onRegisterRefreshRef.current = onRegisterRefresh;
  }, [onRegisterRefresh]);
  React.useEffect(() =>
  {
    onRegisterRefreshRef.current?.((conversationIds?: number[]) => loadData(true, conversationIds));
  }, [loadData]);

  // Listen for sidebar drag-to-organize MOVE: remove the card from its source group visually
  React.useEffect(() =>
  {
    const handler = (e: Event) =>
    {
      const { conversationId, groupId } = (
        e as CustomEvent<{ conversationId: number; groupId: number }>
      ).detail;
      setGroups(prev =>
        prev.map(g =>
        {
          if (g.DashboardGroupId !== groupId) return g;
          return {
            ...g,
            Conversations: g.Conversations.filter(c => c.ConversationId !== conversationId),
          };
        }),
      );
      setColumnTotals(prev => ({
        ...prev,
        [groupId]: Math.max(0, (prev[groupId] || 0) - 1),
      }));
    };
    window.addEventListener('corgee:card-removed-from-group', handler);
    return () => window.removeEventListener('corgee:card-removed-from-group', handler);
  }, []);

  // Track which card is being dragged (drives isMultiDragActive)
  React.useEffect(() =>
  {
    const onStart = (e: Event) =>
    {
      setActiveCardId((e as CustomEvent<{ conversationId: number }>).detail.conversationId);
    };
    const onEnd = () => setActiveCardId(null);
    window.addEventListener('corgee:card-drag-start', onStart);
    window.addEventListener('corgee:card-drag-end', onEnd);
    return () =>
    {
      window.removeEventListener('corgee:card-drag-start', onStart);
      window.removeEventListener('corgee:card-drag-end', onEnd);
    };
  }, []);

  React.useEffect(() =>
  {
    const handler = (e: Event) =>
    {
      const event = e as CustomEvent<{ conversationIds?: (number | string)[]; conversation?: any }>;
      const conversationIds = (event.detail?.conversationIds ?? []).map(id => Number(id));
      const payloadConversation = event.detail?.conversation;

      // No affected ids provided: fall back to a full reload for safety
      if (conversationIds.length === 0)
      {
        loadData(true);
        return;
      }

      // If the event carries the fresh conversation, update from the payload instead
      // of issuing another GetConversationById request for the same conversation.
      if (payloadConversation && payloadConversation.ConversationId)
      {
        const payloadId = Number(payloadConversation.ConversationId);
        setGroups(prev =>
          prev.map(group => ({
            ...group,
            Conversations: group.Conversations.map(conv =>
              conv.ConversationId === payloadId
                ? { ...conv, Phases: payloadConversation.Phases, HasPhases: payloadConversation.HasPhases }
                : conv,
            ),
          })),
        );
        return;
      }

      const token = authStorage.getAccessToken() || '';
      const apiUrl = envConfig.api.baseUrl;

      // Refresh only the affected conversations instead of the whole dashboard
      Promise.all(
        conversationIds.map(conversationId =>
          conversationApi
            .getConversationById(apiUrl, token, Number(conversationId), {
              getAllReplies: false,
              isConversationDetailsView: false,
            })
            .then((updatedConv: any) => ({ conversationId: Number(conversationId), updatedConv }))
            .catch(err =>
            {
              console.error('Failed to reload conversation after phase creation:', err);
              return null;
            }),
        ),
      ).then(results =>
      {
        const updates = new Map<number, any>();
        for (const r of results)
        {
          if (r) updates.set(r.conversationId, r.updatedConv);
        }
        if (updates.size === 0) return;

        setGroups(prev =>
          prev.map(group => ({
            ...group,
            Conversations: group.Conversations.map(conv =>
              // GetConversationById omits DashboardId; keep the one stamped at load time
              updates.has(conv.ConversationId)
                ? { ...updates.get(conv.ConversationId), DashboardId: conv.DashboardId }
                : conv,
            ),
          })),
        );
      });
    };
    window.addEventListener('corgee:phases-created', handler);
    return () => window.removeEventListener('corgee:phases-created', handler);
  }, [loadData]);

  React.useEffect(() =>
  {
    const handler = (e: Event) =>
    {
      const event = e as CustomEvent<{ conversationId: number }>;
      const { conversationId } = event.detail;

      const token = authStorage.getAccessToken() || '';
      const apiUrl = envConfig.api.baseUrl;

      conversationApi
        .getConversationById(apiUrl, token, conversationId, {
          getAllReplies: false,
          isConversationDetailsView: false,
        })
        .then((updatedConv: any) =>
        {
          setGroups(prev =>
            prev.map(group => ({
              ...group,
              Conversations: group.Conversations.map(conv =>
                conv.ConversationId === conversationId
                  ? { ...updatedConv, DashboardId: conv.DashboardId }
                  : conv,
              ),
            })),
          );
        })
        .catch(err =>
        {
          console.error('Failed to reload conversation:', err);
          loadData(true);
        });
    };
    window.addEventListener('corgee:reload-conversation', handler);
    return () => window.removeEventListener('corgee:reload-conversation', handler);
  }, [loadData]);

  // Listen for phase updates (when phases are added or removed)
  React.useEffect(() =>
  {
    const handler = (e: Event) =>
    {
      const event = e as CustomEvent<{ conversationId: string }>;
      const { conversationId } = event.detail;

      const token = authStorage.getAccessToken() || '';
      const apiUrl = envConfig.api.baseUrl;

      conversationApi
        .getConversationById(apiUrl, token, Number(conversationId), {
          getAllReplies: false,
          isConversationDetailsView: false,
        })
        .then((updatedConv: any) =>
        {
          setGroups(prev =>
            prev.map(group => ({
              ...group,
              Conversations: group.Conversations.map(conv =>
                conv.ConversationId === Number(conversationId)
                  ? { ...updatedConv, DashboardId: conv.DashboardId }
                  : conv,
              ),
            })),
          );
        })
        .catch(err =>
        {
          console.error('Failed to reload conversation after phase update:', err);
          loadData(true);
        });
    };
    window.addEventListener('corgee:conversation-phases-updated', handler);
    return () => window.removeEventListener('corgee:conversation-phases-updated', handler);
  }, [loadData]);

  // Listen for conversation updates (e.g., when a related card is created)
  React.useEffect(() =>
  {
    const unsubscribe = conversationBus.on('conversation-updated', data =>
    {
      if (data.updates?.refreshCustomFields || data.updates?.relatedCardCreated)
      {
        const { conversationId } = data;
        const token = authStorage.getAccessToken() || '';
        const apiUrl = envConfig.api.baseUrl;

        const infoWindowNewConv = data.updates?.relatedCardCreated ? (data.newConversationData ?? null) : null;
        const infoWindowNewConvId: number | null = infoWindowNewConv
          ? (infoWindowNewConv.ConversationId ?? infoWindowNewConv.Conversation?.ConversationId ?? null)
          : null;

        conversationApi
          .getConversationById(apiUrl, token, conversationId, {
            getAllReplies: false,
            isConversationDetailsView: false,
          })
          .then((updatedConv: any) =>
          {
            setGroups(prev =>
              prev.map(group =>
              {
                const hasTarget = group.Conversations.some(c => c.ConversationId === conversationId);
                let convs = group.Conversations.map(conv =>
                  conv.ConversationId === conversationId
                    ? {
                      ...updatedConv,
                      ParentConversationId: conv.ParentConversationId,
                      ...(group.IsDefault && { IsParent: false }),
                    }
                    : { ...conv },
                );

                if (!hasTarget) return { ...group, Conversations: convs };

                // If this is an InfoWindow create, add the new conv if not already in this group
                if (infoWindowNewConvId && !convs.some(c => c.ConversationId === infoWindowNewConvId))
                {
                  const newConv = {
                    ...(infoWindowNewConv.Conversation ?? infoWindowNewConv),
                    DashboardGroupId: group.DashboardGroupId,
                  };

                  if (group.IsDefault)
                  {
                    // Dynamic column: prepend at top, no parent nesting
                    convs = [newConv, ...convs];
                  }
                  else
                  {
                    // Normal column: insert as child and mark source as IsParent
                    convs = [
                      ...convs.map(c =>
                        c.ConversationId === conversationId ? { ...c, IsParent: true } : c,
                      ),
                      { ...newConv, ParentConversationId: conversationId },
                    ];
                    setExpandedParentIds(prev =>
                    {
                      const next = new Set(prev);
                      next.add(conversationId);
                      return next;
                    });
                  }
                }

                // Cascade progress changes up the ancestor chain after the API update
                let changed = true;
                while (changed)
                {
                  changed = false;
                  for (const conv of convs)
                  {
                    if (!conv.IsParent) continue;
                    const children = convs.filter((c: any) => c.ParentConversationId === conv.ConversationId);
                    if (children.length === 0) continue;
                    const avg = Math.round(
                      children.reduce((sum: number, c: any) => sum + (c.IsParent ? (c.Progress ?? 0) : (c.Finished ? 100 : 0)), 0) / children.length,
                    );
                    if (conv.Progress !== avg) { conv.Progress = avg; changed = true; }
                  }
                }

                return { ...group, Conversations: convs };
              }),
            );
          })
          .catch(err =>
          {
            console.error('Failed to reload conversation after update:', err);
          });
      }
    });

    return () => unsubscribe();
  }, []);

  // Sync title edits originating from the InfoWindow back to the dashboard cards
  React.useEffect(() =>
  {
    const unsubscribe = conversationBus.on('title-updated', data =>
    {
      setGroups(prev =>
        prev.map(group => ({
          ...group,
          Conversations: group.Conversations.map(conv =>
            conv.ConversationId === data.conversationId
              ? { ...conv, Title: data.title }
              : conv,
          ),
        })),
      );
    });

    return () => unsubscribe();
  }, []);

  // Ctrl+Click handler: toggle card in/out of selection
  const handleCardCtrlClick = useCallback((cardId: number, shiftKey: boolean, groupId: number) =>
  {
    if (shiftKey)
    {
      const group = groupsRef.current.find(g => g.DashboardGroupId === groupId);
      if (!group) return;
      const conversations = group.Conversations;

      if (selectedCardIdsRef.current.size > 0)
      {
        // ctrl+shift+click with existing selection: range select
        const firstSelectedId = [...selectedCardIdsRef.current][0];
        const firstIdx = conversations.findIndex((c: any) => c.ConversationId === firstSelectedId);
        const lastIdx  = conversations.findIndex((c: any) => c.ConversationId === cardId);
        if (firstIdx === -1 || lastIdx === -1) return;
        const start = Math.min(firstIdx, lastIdx);
        const end   = Math.max(firstIdx, lastIdx);
        const rangeIds = conversations.slice(start, end + 1).map((c: any) => c.ConversationId);
        setSelectedCardIds(new Set(rangeIds));
      }
      else
      {
        // ctrl+shift+click with no prior selection: select all in column
        const allIds = conversations.map((c: any) => c.ConversationId);
        setSelectedCardIds(new Set(allIds));
      }
      return;
    }

    // ctrl+click: toggle single card
    setSelectedCardIds(prev =>
    {
      const next = new Set(prev);
      if (next.has(cardId))
      {
        next.delete(cardId);
      }
      else
      {
        next.add(cardId);
      }
      return next;
    });
  }, []);

  const handleCardShiftClick = useCallback((cardId: number, groupId: number) =>
  {
    // shift+click alone: range selection from first selected to clicked
    if (selectedCardIdsRef.current.size === 0) return;

    const group = groupsRef.current.find(g => g.DashboardGroupId === groupId);
    if (!group) return;

    const conversations = group.Conversations;
    const firstSelectedId = [...selectedCardIdsRef.current][0];
    const firstIdx = conversations.findIndex((c: any) => c.ConversationId === firstSelectedId);
    const lastIdx  = conversations.findIndex((c: any) => c.ConversationId === cardId);

    if (firstIdx === -1 || lastIdx === -1) return;

    const start = Math.min(firstIdx, lastIdx);
    const end   = Math.max(firstIdx, lastIdx);
    const rangeIds = conversations
      .slice(start, end + 1)
      .map((c: any) => c.ConversationId);

    setSelectedCardIds(new Set(rangeIds));
  }, []);

  const handleSelectAllCards = useCallback((cardIds: number[]) =>
  {
    setSelectedCardIds(new Set(cardIds));
  }, []);

  const getSelectedConversations = useCallback((): ConversationDetail[] =>
  {
    const result: ConversationDetail[] = [];
    for (const group of groupsRef.current)
    {
      for (const conv of group.Conversations)
      {
        if (selectedCardIdsRef.current.has(conv.ConversationId))
        {
          result.push(conv);
        }
      }
    }
    return result;
  }, []);

  // Keep global selected-card titles in sync so the drag ghost can show all selected cards
  useEffect(() =>
  {
    if (selectedCardIds.size === 0)
    {
      setSelectedCardTitles([]);
      return;
    }
    const titles: Array<{ id: number; title: string; layerColor?: string; groupId?: number }> = [];
    for (const group of groups)
    {
      for (const conv of group.Conversations)
      {
        if (selectedCardIds.has(conv.ConversationId))
        {
          titles.push({
            id: conv.ConversationId,
            title: conv.Title,
            layerColor: (conv as any).LayerColor,
            groupId: group.DashboardGroupId,
          });
        }
      }
    }
    setSelectedCardTitles(titles);
  }, [selectedCardIds, groups]);

  // Clear selection on click anywhere in the groups container (without Ctrl).
  // Guard against portal events that bubble through the React tree but originate
  // outside the container DOM (e.g. modal buttons, action-menu items).
  const handleContainerClick = useCallback(
    (e: React.MouseEvent) =>
    {
      const isInsideContainer = (e.currentTarget as HTMLElement).contains(e.target as Node);
      if (!e.ctrlKey && !e.metaKey && !e.shiftKey && selectedCardIds.size > 0 && isInsideContainer)
      {
        setSelectedCardIds(new Set());
      }
    },
    [selectedCardIds.size],
  );

  // Pure helper: recalculates Progress (0–100) on every IsParent card in a conversations array
  // based on how many of its loaded children are Finished.
  const recalcProgressForParents = useCallback(
    <
      T extends {
        ConversationId: number;
        ParentConversationId?: number | null;
        IsParent?: boolean;
        Finished?: boolean;
        Progress?: number;
      },
    >(
      convs: T[],
    ): T[] =>
    {
      return convs.map(conv =>
      {
        if (!conv.IsParent) return conv;
        const children = convs.filter(c => c.ParentConversationId === conv.ConversationId);
        if (children.length === 0) return conv;
        const finishedCount = children.filter(c => c.Finished).length;
        const newProgress = Math.round((finishedCount / children.length) * 100);
        if (newProgress === conv.Progress) return conv;
        return { ...conv, Progress: newProgress };
      });
    },
    [],
  );

  // Pure helper: removes conversations from a group's array and fixes their former parents
  // (IsParent → false when no children remain, Progress recalculated) so the expand icon updates.
  const removeConversationsAndFixParents = useCallback(
    (convs: ConversationDetail[], idSet: Set<number>): ConversationDetail[] =>
    {
      const affectedParentIds = new Set<number>();
      convs.forEach(c =>
      {
        if (idSet.has(c.ConversationId) && c.ParentConversationId)
          affectedParentIds.add(c.ParentConversationId);
      });
      const filtered = convs.filter(c => !idSet.has(c.ConversationId));
      if (affectedParentIds.size === 0) return filtered;
      const withParentFix = filtered.map(c =>
      {
        if (!c.IsParent || !affectedParentIds.has(c.ConversationId)) return c;
        const stillHasChildren = filtered.some(x => x.ParentConversationId === c.ConversationId);
        return stillHasChildren ? c : { ...c, IsParent: false };
      });
      return recalcProgressForParents(withParentFix);
    },
    [recalcProgressForParents],
  );

  // react-dnd drop callbacks — called by DashboardGroupColumn when a drop is resolved
  const handleCardDropped = useCallback(
    (
      draggedCardId: number,
      sourceGroupId: number,
      finalGroupId: number,
      overCardId: number | null,
    ) =>
    {
      // Multi-card drag: dragged card is part of the current selection (cross-column only)
      const isMultiDrag =
        selectedCardIds.has(draggedCardId) &&
        selectedCardIds.size > 1 &&
        finalGroupId !== sourceGroupId;

      if (isMultiDrag)
      {
        setGroups(prevGroups =>
        {
          // Collect every selected card and the group it currently lives in
          type CardWithSource = { card: ConversationDetail; fromGroupId: number };
          const cardsToMove: CardWithSource[] = [];
          for (const cardId of selectedCardIds)
          {
            for (const g of prevGroups)
            {
              const card = g.Conversations.find(c => c.ConversationId === cardId);
              if (card)
              {
                cardsToMove.push({ card, fromGroupId: g.DashboardGroupId });
                break;
              }
            }
          }
          if (cardsToMove.length === 0) return prevGroups;

          const movedIds = new Set(cardsToMove.map(x => x.card.ConversationId));

          const newGroups = prevGroups.map(group =>
          {
            if (group.DashboardGroupId === finalGroupId)
            {
              // Remove any cards already present in destination, then insert moved cards
              const existing = group.Conversations.filter(c => !movedIds.has(c.ConversationId));
              const movedCards = cardsToMove.map(({ card }) => ({
                ...card,
                DashboardGroupId: finalGroupId,
                ParentConversationId: 0,
              }));
              const insertIdx = overCardId
                ? existing.findIndex(c => c.ConversationId === overCardId)
                : 0;
              const result = [...existing];
              result.splice(insertIdx === -1 ? 0 : insertIdx, 0, ...movedCards);
              return { ...group, Conversations: recalcProgressForParents(result) };
            }
            // Remove selected cards from every other group
            if (group.Conversations.some(c => movedIds.has(c.ConversationId)))
            {
              const filtered = group.Conversations.filter(c => !movedIds.has(c.ConversationId));
              // A former parent whose only children were moved away is no longer a parent
              const withParentFix = filtered.map(c =>
              {
                if (!c.IsParent) return c;
                const stillHasChildren = filtered.some(
                  x => x.ParentConversationId === c.ConversationId,
                );
                return stillHasChildren ? c : { ...c, IsParent: false };
              });
              return { ...group, Conversations: recalcProgressForParents(withParentFix) };
            }
            return group;
          });

          // Fire one API call per card
          for (const { card, fromGroupId } of cardsToMove)
          {
            if (fromGroupId === finalGroupId) continue;
            const oldParentId =
              card.ParentConversationId && card.ParentConversationId !== 0
                ? card.ParentConversationId
                : null;
            if (oldParentId)
            {
              dashboardGroupsService
                .insertDashboardGroupConversation({
                  DashboardId: dashboardId,
                  DashboardGroupId: finalGroupId,
                  ConversationId: card.ConversationId,
                  ParentConversationId: 0,
                  PreviousParentId: oldParentId,
                  socketConnectionId: socketConnectionId || '',
                })
                .catch(err => console.error('Error un-nesting card (multi):', err));
            }
            else
            {
              dashboardGroupsService
                .moveConversationsToGroup({
                  dashboardId,
                  destinationGroupId: finalGroupId,
                  originGroupId: fromGroupId,
                  movedConversationId: card.ConversationId,
                })
                .catch(err => console.error('Error moving card (multi):', err));
            }
          }

          if (onCardMoved) onCardMoved(draggedCardId, sourceGroupId, finalGroupId);
          return newGroups;
        });

        // Remove all moved cards from expanded set
        setExpandedParentIds(prev =>
        {
          const movedIds = new Set([...selectedCardIds]);
          if (![...movedIds].some(id => prev.has(id))) return prev;
          const next = new Set(prev);
          for (const id of movedIds) next.delete(id);
          return next;
        });

        // Update column totals: approximate (accurate counts sync on next reload)
        setColumnTotals(prev =>
        {
          const next = { ...prev };
          next[sourceGroupId] = Math.max(0, (next[sourceGroupId] || 0) - selectedCardIds.size);
          next[finalGroupId] = (next[finalGroupId] || 0) + selectedCardIds.size;
          return next;
        });

        setSelectedCardIds(new Set());
        return;
      }

      // ── Single-card drag (original logic) ──────────────────────────────────

      // Pre-compute loaded descendants so cross-column moves bring them along
      // and column totals stay accurate. Uses groupsRef (sync) not state (async).
      const crossColumnDescendantIds = new Set<number>();
      if (finalGroupId !== sourceGroupId)
      {
        const srcGroup = groupsRef.current.find(g => g.DashboardGroupId === sourceGroupId);
        if (srcGroup)
        {
          const queue = [draggedCardId];
          while (queue.length > 0)
          {
            const pid = queue.shift()!;
            srcGroup.Conversations.forEach(c =>
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              if ((c as any).ParentConversationId === pid)
              {
                crossColumnDescendantIds.add(c.ConversationId);
                queue.push(c.ConversationId);
              }
            });
          }
        }
      }

      setGroups(prevGroups =>
      {
        // Find dragged card to detect if it was a child
        const sourceGroup = prevGroups.find(g => g.DashboardGroupId === sourceGroupId);
        const draggedCard = sourceGroup?.Conversations.find(
          c => c.ConversationId === draggedCardId,
        );
        if (!draggedCard) return prevGroups;

        const oldParentId =
          draggedCard.ParentConversationId && draggedCard.ParentConversationId !== 0
            ? draggedCard.ParentConversationId
            : null;

        if (finalGroupId === sourceGroupId)
        {
          // Same-column reorder / un-nest
          if (overCardId === draggedCardId) return prevGroups;

          // Dropped on empty column area with no overCard — only meaningful if the card
          // was nested (oldParentId set); un-nest it and move to end of root list.
          if (!overCardId)
          {
            if (!oldParentId) return prevGroups;
            const result = prevGroups.map(group =>
            {
              if (group.DashboardGroupId !== finalGroupId) return group;
              const convs = group.Conversations.map(c =>
              {
                if (c.ConversationId === draggedCardId) return { ...c, ParentConversationId: 0 };
                if (c.ConversationId === oldParentId)
                {
                  const stillHasChildren = group.Conversations.some(
                    x =>
                      x.ParentConversationId === oldParentId && x.ConversationId !== draggedCardId,
                  );
                  return stillHasChildren ? c : { ...c, IsParent: false };
                }
                return c;
              });
              // Move the un-nested card to the end of the conversations array
              const idx = convs.findIndex(c => c.ConversationId === draggedCardId);
              if (idx !== -1)
              {
                const card = convs.splice(idx, 1)[0];
                if (card) convs.push(card);
              }
              return { ...group, Conversations: recalcProgressForParents(convs) };
            });
            dashboardGroupsService
              .insertDashboardGroupConversation({
                DashboardId: dashboardId,
                DashboardGroupId: finalGroupId,
                ConversationId: draggedCardId,
                ParentConversationId: 0,
                PreviousParentId: oldParentId,
                socketConnectionId: socketConnectionId || '',
              })
              .catch(err => console.error('Error un-nesting card:', err));
            return result;
          }

          const result = prevGroups.map(group =>
          {
            if (group.DashboardGroupId !== finalGroupId) return group;
            // Un-nest the card and clean old parent's IsParent if applicable
            const convs = group.Conversations.map(c =>
            {
              if (c.ConversationId === draggedCardId)
              {
                return oldParentId ? { ...c, ParentConversationId: 0 } : c;
              }
              if (oldParentId && c.ConversationId === oldParentId)
              {
                const stillHasChildren = group.Conversations.some(
                  x => x.ParentConversationId === oldParentId && x.ConversationId !== draggedCardId,
                );
                return stillHasChildren ? c : { ...c, IsParent: false };
              }
              return c;
            });
            const oldIdx = convs.findIndex(c => c.ConversationId === draggedCardId);
            const newIdx = convs.findIndex(c => c.ConversationId === overCardId);
            if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return group;
            const reordered = arrayMove(convs, oldIdx, newIdx);
            return { ...group, Conversations: recalcProgressForParents(reordered) };
          });

          // API call for un-nesting (same-column)
          if (oldParentId)
          {
            dashboardGroupsService
              .insertDashboardGroupConversation({
                DashboardId: dashboardId,
                DashboardGroupId: finalGroupId,
                ConversationId: draggedCardId,
                ParentConversationId: 0,
                PreviousParentId: oldParentId,
                socketConnectionId: socketConnectionId || '',
              })
              .catch(err => console.error('Error un-nesting card:', err));
          }

          return result;
        }

        // Cross-column: optimistic state update
        const descendantCards =
          crossColumnDescendantIds.size > 0 && sourceGroup
            ? sourceGroup.Conversations.filter(c => crossColumnDescendantIds.has(c.ConversationId))
            : [];
        const newGroups = prevGroups.map(group =>
        {
          if (group.DashboardGroupId === sourceGroupId)
          {
            // Remove parent AND all its loaded descendants
            let convs = group.Conversations.filter(
              c =>
                c.ConversationId !== draggedCardId &&
                !crossColumnDescendantIds.has(c.ConversationId),
            );
            // Clean IsParent of old parent if it lost its last child
            if (oldParentId)
            {
              convs = convs.map(c =>
              {
                if (c.ConversationId === oldParentId)
                {
                  const stillHasChildren = convs.some(x => x.ParentConversationId === oldParentId);
                  return stillHasChildren ? c : { ...c, IsParent: false };
                }
                return c;
              });
            }
            // Recalc Progress for any parent that lost a child
            return { ...group, Conversations: recalcProgressForParents(convs) };
          }
          if (group.DashboardGroupId === finalGroupId)
          {
            const movedCard = {
              ...draggedCard,
              DashboardGroupId: finalGroupId,
              ParentConversationId: 0,
            };
            // Descendants keep their ParentConversationId (still points to the moved parent)
            const movedDescendants = descendantCards.map(c => ({
              ...c,
              DashboardGroupId: finalGroupId,
            }));
            const convs = [...group.Conversations];
            const insertIdx = overCardId
              ? convs.findIndex(c => c.ConversationId === overCardId)
              : 0;
            convs.splice(insertIdx === -1 ? 0 : insertIdx, 0, movedCard, ...movedDescendants);
            return { ...group, Conversations: convs };
          }
          return group;
        });

        // API call — use response to update Progress on parent cards live
        if (oldParentId)
        {
          dashboardGroupsService
            .insertDashboardGroupConversation({
              DashboardId: dashboardId,
              DashboardGroupId: finalGroupId,
              ConversationId: draggedCardId,
              ParentConversationId: 0,
              PreviousParentId: oldParentId,
              socketConnectionId: socketConnectionId || '',
            })
            .catch(err => console.error('Error un-nesting card:', err));
        }
        else
        {
          dashboardGroupsService
            .moveConversationsToGroup({
              dashboardId,
              destinationGroupId: finalGroupId,
              originGroupId: sourceGroupId,
              movedConversationId: draggedCardId,
            })
            .then(response =>
            {
              if (response && response.length > 0)
              {
                applyProgressUpdatesFromResponse(response);
              }
            })
            .catch(err => console.error('Error moving card:', err));
        }

        if (onCardMoved) onCardMoved(draggedCardId, sourceGroupId, finalGroupId);
        return newGroups;
      });

      // Remove moved card (and any moved descendants) from expanded set
      setExpandedParentIds(prev =>
      {
        const toRemove = [draggedCardId, ...crossColumnDescendantIds];
        if (!toRemove.some(id => prev.has(id))) return prev;
        const next = new Set(prev);
        toRemove.forEach(id => next.delete(id));
        return next;
      });

      if (finalGroupId !== sourceGroupId)
      {
        const totalMoved = 1 + crossColumnDescendantIds.size;
        setColumnTotals(prev => ({
          ...prev,
          [sourceGroupId]: Math.max(0, (prev[sourceGroupId] || 0) - totalMoved),
          [finalGroupId]: (prev[finalGroupId] || 0) + totalMoved,
        }));
      }
      setSelectedCardIds(new Set());
    },
    [dashboardId, onCardMoved, socketConnectionId, recalcProgressForParents, selectedCardIds],
  );

  const handleInfoWindowCardDrop = useCallback(
    async (conversation: ConversationDetail, targetGroupId: number) =>
    {
      const conversationId = conversation.ConversationId;
      let sourceGroupId: number | null = null;

      // Optimistic update: remove from old group, add at end of target group
      setGroups(prevGroups =>
      {
        // Locate existing group
        for (const g of prevGroups)
        {
          if (g.Conversations.some(c => c.ConversationId === conversationId))
          {
            sourceGroupId = g.DashboardGroupId;
            break;
          }
        }

        const updatedCard = {
          ...conversation,
          DashboardGroupId: targetGroupId,
          ParentConversationId: 0,
        };
        const newGroups = prevGroups.map(group =>
        {
          if (group.DashboardGroupId === targetGroupId)
          {
            // Remove any existing instance then append at end
            const filtered = group.Conversations.filter(c => c.ConversationId !== conversationId);
            const result = [...filtered, updatedCard];
            return { ...group, Conversations: result };
          }
          // Remove from source group if it was there
          if (sourceGroupId !== null && group.DashboardGroupId === sourceGroupId)
          {
            return {
              ...group,
              Conversations: group.Conversations.filter(c => c.ConversationId !== conversationId),
            };
          }
          return group;
        });
        return newGroups;
      });

      // Update column totals only when moving between groups
      setColumnTotals(prev =>
      {
        if (sourceGroupId === null || sourceGroupId === targetGroupId) return prev;
        return {
          ...prev,
          [sourceGroupId]: Math.max(0, (prev[sourceGroupId] || 1) - 1),
          [targetGroupId]: (prev[targetGroupId] || 0) + 1,
        };
      });

      // API: move between groups or insert new
      try
      {
        if (sourceGroupId !== null && sourceGroupId !== targetGroupId)
        {
          await dashboardGroupsService.moveConversationsToGroup({
            dashboardId,
            destinationGroupId: targetGroupId,
            originGroupId: sourceGroupId,
            movedConversationId: conversationId,
          });
        }
        else
        {
          await dashboardGroupsService.insertDashboardGroupConversation({
            DashboardId: dashboardId,
            DashboardGroupId: targetGroupId,
            ConversationId: conversationId,
            ParentConversationId: 0,
            PreviousParentId: null,
            socketConnectionId: socketConnectionId || '',
          });
        }
      }
      catch (err)
      {
        console.error('Error dropping InfoWindow card to column:', err);
        // Rollback: restore previous state
        setGroups(prevGroups =>
          prevGroups.map(group =>
          {
            if (group.DashboardGroupId === targetGroupId)
            {
              return {
                ...group,
                Conversations: group.Conversations.filter(c => c.ConversationId !== conversationId),
              };
            }
            if (sourceGroupId !== null && group.DashboardGroupId === sourceGroupId)
            {
              return { ...group, Conversations: [...group.Conversations, conversation] };
            }
            return group;
          }),
        );
        setColumnTotals(prev =>
        {
          if (sourceGroupId === null || sourceGroupId === targetGroupId) return prev;
          return {
            ...prev,
            [sourceGroupId]: (prev[sourceGroupId] || 0) + 1,
            [targetGroupId]: Math.max(0, (prev[targetGroupId] || 1) - 1),
          };
        });
      }
    },
    [dashboardId, socketConnectionId],
  );

  const handleColumnDropped = useCallback(
    (fromIndex: number, toIndex: number) =>
    {
      if (fromIndex === toIndex) return;
      setGroups(prevGroups =>
      {
        const newGroups = arrayMove(prevGroups, fromIndex, toIndex);

        let currentX = 0;
        const groupsToUpdate = newGroups.map(g =>
        {
          const sizeX = g.sizeX || 3;
          const position = currentX;
          currentX += sizeX;
          return {
            ...g,
            SizeX: sizeX,
            sizeX,
            SizeY: g.sizeY || 0,
            sizeY: g.sizeY || 0,
            Row: g.row || 0,
            row: g.row || 0,
            Col: position,
            positionX: position,
            col: position,
            TotalItems: g.Conversations?.length || 0,
            UpdateGroup: true,
            SocketConnectionId: socketConnectionId || g.SocketConnectionId || '',
          };
        });
        dashboardGroupsService.updateDashboardGroupPosition(groupsToUpdate).catch(console.error);
        return newGroups;
      });
    },
    [socketConnectionId],
  );

  // Handle column name change
  const handleColumnNameChange = useCallback(
    async (groupId: number, newName: string) =>
    {
      const group = groups.find(g => g.DashboardGroupId === groupId);
      if (!group) return;

      // Optimistic update
      const newGroups = groups.map(g =>
        g.DashboardGroupId === groupId ? { ...g, Name: newName } : g,
      );
      setGroups(newGroups);

      // Call API
      const success = await dashboardGroupsService.updateDashboardGroup({
        DashboardGroupId: groupId,
        DashboardId: group.DashboardId,
        Name: newName,
        IsDefault: group.IsDefault,
        SizeX: group.sizeX || 3,
        SizeY: group.sizeY || 0,
        Row: group.row || 0,
        Col: group.col || 0, // Should be current col index
        showAllDetails: group.showAllDetails || false,
        Conversations: [], // API expects empty or existing? User payload says []
        positionX: group.col || 0, // Using col as positionX
        TotalItems: group.Conversations?.length || 0,
        col: group.col || 0,
        minSizeX: 3, // Default?
        showProjectName: !hideProjectName,
        isLoading: false,
        minSizeY: 3, // Default?
        row: group.row || 0,
        sizeX: group.sizeX || 3,
        sizeY: group.sizeY || 0,
        draftName: newName,
        editGroupName: false,
        SocketConnectionId: socketConnectionId || group.SocketConnectionId || '',
      });

      if (!success)
      {
        // Revert if API fails
        const revertedGroups = groups.map(g =>
          g.DashboardGroupId === groupId ? { ...g, Name: group.Name } : g,
        );
        setGroups(revertedGroups);
      }
    },
    [groups, hideProjectName, socketConnectionId],
  );

  // Add new task/card handler
  const handleAddNewTask = useCallback((_e: React.MouseEvent, group: DashboardGroupData) =>
  {
    setAddingTaskGroupId(prev => (prev === group.DashboardGroupId ? null : group.DashboardGroupId));
  }, []);

  const handleCancelAddTask = useCallback(() =>
  {
    setAddingTaskGroupId(null);
  }, []);

  const handleTaskAdded = useCallback(
    async (task: any) =>
    {
      if (!task) return;

      const groupId = task.DashboardGroupId;
      const targetGroup = groupsRef.current.find(g => g.DashboardGroupId === groupId);
      const alreadyExists = targetGroup?.Conversations.some(
        c => c.ConversationId === task.ConversationId,
      ) ?? false;

      if (targetGroup && !targetGroup.IsDefault && !alreadyExists)
      {
        setColumnTotals(t => ({ ...t, [groupId]: (t[groupId] || 0) + 1 }));
      }

      setGroups(prev =>
        prev.map(g =>
        {
          if (g.DashboardGroupId !== groupId) return g;
          if (g.Conversations.some(c => c.ConversationId === task.ConversationId)) return g;
          return { ...g, Conversations: [task, ...g.Conversations] };
        }),
      );
    },
    [configValueId, searchText, filters],
  );

  // Add new column handler
  const handleAddColumn = useCallback(async () =>
  {
    // Calculate col position based on last column
    const lastGroup = groups[groups.length - 1];
    const newCol = lastGroup ? lastGroup.col + lastGroup.sizeX : 0;

    const params = {
      DashboardId: dashboardId,
      Conversations: [],
      Name: '(Double click to rename)',
      positionX: newCol,
      col: newCol,
      row: 0,
      sizeX: 3,
      sizeY: 0,
      isNew: true,
      TotalItems: 0,
      SocketConnectionId: socketConnectionId,
    };

    const newGroup = await dashboardGroupsService.createDashboardGroup(params);
    if (newGroup)
    {
      setGroups(prev => [...prev, { ...newGroup, Conversations: [] }]);
      setColumnWidths(prev => ({
        ...prev,
        [newGroup.DashboardGroupId]: columnWidth,
      }));
      setColumnTotals(prev => ({
        ...prev,
        [newGroup.DashboardGroupId]: 0,
      }));
    }
  }, [groups, dashboardId, columnWidth]);

  // Create conversation handler
  const handleCreateConversation = useCallback((conv: Conversation, itemType: SideMenuItem) =>
  {
    createSourceConvRef.current = conv;
    editCardInitRef.current?.(conv, itemType);
    editCardRef.current?.openModal();
  }, []);

  const handleCreateItemFromCustomType = useCallback((item: any, sourceConv: ConversationDetail) =>
  {
    const itemType: SideMenuItem = {
      ...item,
      ConfigValueId: item.ConfigValueID ?? item.ConfigValueId,
      RelatedReplyId: 0,
    };
    const conv = {
      ProjectId: sourceConv.ProjectId,
      ConversationId: sourceConv.ConversationId,
      DashboardGroupId: sourceConv.DashboardGroupId,
    } as Conversation;
    editCardInitRef.current?.(conv, itemType);
    editCardRef.current?.openModal();
  }, []);

  // Edit conversation handler — fetches full data first so Attachments are populated
  const handleEditConversation = useCallback(async (_e: any, conv: any) =>
  {
    try
    {
      const fullConv = await conversationService.getConversationDetails(conv.ConversationId);
      editCardInitRef.current?.(fullConv ?? conv, null);
    }
    catch
    {
      editCardInitRef.current?.(conv, null);
    }
    editCardRef.current?.openModal();
  }, []);

  // duplicate conversation handler
  const handleDuplicateConversation = useCallback((_e: any, conv: any) =>
  {
    duplicateCardInitRef.current?.(conv);
    duplicateCardRef.current?.openModal();
  }, []);

  // duplicate conversation handler
  const handleRemoveFromColumn = async (_e: any, conversation: ConversationDetail) =>
  {
    const DashboardGroupIdDefault = groups.find(x => x.IsDefault)?.DashboardGroupId;
    const request = {
      DashboardGroupIdDefault,
      layersToHide: CorgeeStorageHelper.getLayersToHide()?.toString() || '0',
      dashboardGroupConvs: [
        {
          DashboardGroupId: conversation.DashboardGroupId,
          ConversationId: conversation.ConversationId,
          ConfigValueId: conversation.ConfigValueId.toString(),
          SocketConnectionId: socketConnectionId,
        },
      ],
    };
    await conversationService.DeleteDashboardGroupConversations(request);

    setGroups(prev =>
    {
      const allConversations = prev.flatMap(g => g.Conversations);
      return prev.map(group =>
      {
        if (group.IsDefault)
        {
          const copy = allConversations.find(c => c.ConversationId === conversation.ConversationId);
          if (
            !copy ||
            group.Conversations.some(c => c.ConversationId === conversation.ConversationId)
          )
            return group;
          return { ...group, Conversations: [copy, ...group.Conversations] };
        }
        else if (
          group.Conversations.some(c => c.ConversationId === conversation.ConversationId)
        )
        {
          return {
            ...group,
            Conversations: group.Conversations.filter(
              c => c.ConversationId !== conversation.ConversationId,
            ),
          };
        }
        return group;
      });
    });
    setColumnTotals(prev => ({
      ...prev,
      [conversation.DashboardGroupId]: Math.max(0, (prev[conversation.DashboardGroupId] || 0) - 1),
    }));
  };

  const handleBulkRemoveFromColumn = async (conversations: ConversationDetail[]) =>
  {
    if (!conversations.length) return;
    const DashboardGroupIdDefault = groups.find(x => x.IsDefault)?.DashboardGroupId;
    const request = {
      DashboardGroupIdDefault,
      layersToHide: CorgeeStorageHelper.getLayersToHide()?.toString() || '0',
      dashboardGroupConvs: conversations.map(c => ({
        DashboardGroupId: c.DashboardGroupId,
        ConversationId: c.ConversationId,
        ConfigValueId: c.ConfigValueId.toString(),
        SocketConnectionId: socketConnectionId,
      })),
    };
    await conversationService.DeleteDashboardGroupConversations(request);

    const removedIds = new Set(conversations.map(c => c.ConversationId));
    setGroups(prev =>
    {
      const allConversations = prev.flatMap(g => g.Conversations);
      return prev.map(group =>
      {
        if (group.IsDefault)
        {
          const toAdd = allConversations.filter(
            c =>
              removedIds.has(c.ConversationId) &&
              !group.Conversations.some(gc => gc.ConversationId === c.ConversationId),
          );
          if (!toAdd.length) return group;
          return { ...group, Conversations: [...toAdd, ...group.Conversations] };
        }
        else if (group.Conversations.some(c => removedIds.has(c.ConversationId)))
        {
          return {
            ...group,
            Conversations: group.Conversations.filter(c => !removedIds.has(c.ConversationId)),
          };
        }
        return group;
      });
    });
    const decrements: Record<number, number> = {};
    conversations.forEach(c =>
    {
      decrements[c.DashboardGroupId] = (decrements[c.DashboardGroupId] || 0) + 1;
    });
    setColumnTotals(prev =>
    {
      const next = { ...prev };
      Object.entries(decrements).forEach(([gId, count]) =>
      {
        next[Number(gId)] = Math.max(0, (prev[Number(gId)] || 0) - count);
      });
      return next;
    });
  };

  const handleConversationProjectChanged = useCallback((conversationId: number) =>
  {
    const ownerGroup = groupsRef.current.find(g =>
      g.Conversations.some(c => c.ConversationId === conversationId),
    );
    if (ownerGroup && !ownerGroup.IsDefault)
    {
      setColumnTotals(t => ({
        ...t,
        [ownerGroup.DashboardGroupId]: Math.max(0, (t[ownerGroup.DashboardGroupId] || 0) - 1),
      }));
    }
    setGroups(prev =>
      prev.map(group => ({
        ...group,
        Conversations: group.Conversations.filter(c => c.ConversationId !== conversationId),
      })),
    );
  }, []);

  /*
   * Changing a card's project used to remove it from the board, so the cards vanished until
   * the page was refreshed. They now stay put and show the new project instead.
   *
   * The one case where removal is still correct is a project-scoped dashboard: a card moved to
   * a different project no longer belongs there. General dashboards and Card Types aren't
   * project-scoped, so nothing is removed for them.
   */
  const handleConversationsProjectChanged = (
    changes: Array<{ ConversationId: number; ProjectId: number; ProjectName: string }>,
  ) =>
  {
    if (!changes.length) return;

    const changeById = new Map(changes.map(c => [c.ConversationId, c]));

    const dashboardProjectId = Number(projectId) || 0;
    const removedIds = new Set<number>();

    if (dashboardProjectId > 0)
    {
      changes.forEach(c =>
      {
        if (c.ProjectId !== dashboardProjectId) removedIds.add(c.ConversationId);
      });
    }

    if (removedIds.size > 0)
    {
      const groupRemovals: Record<number, number> = {};
      groupsRef.current.forEach(group =>
      {
        const removed = group.Conversations.filter(c => removedIds.has(c.ConversationId)).length;
        if (removed > 0 && !group.IsDefault) groupRemovals[group.DashboardGroupId] = removed;
      });

      if (Object.keys(groupRemovals).length > 0)
      {
        setColumnTotals(t =>
        {
          const next = { ...t };
          for (const [gid, count] of Object.entries(groupRemovals))
          {
            next[Number(gid)] = Math.max(0, (t[Number(gid)] || 0) - count);
          }
          return next;
        });
      }
    }

    setGroups(prev =>
      prev.map(group => ({
        ...group,
        Conversations: group.Conversations
          .filter(c => !removedIds.has(c.ConversationId))
          .map(c =>
          {
            const change = changeById.get(c.ConversationId);
            if (!change) return c;

            // reactSelfUpdate mirrors handleUpdateConversation — it is what surfaces the
            // blue "updated details" confirmation on the card.
            return {
              ...c,
              ProjectId: change.ProjectId,
              ProjectName: change.ProjectName,
              reactSelfUpdate: true,
            };
          }),
      })),
    );

    setSelectedCardIds(new Set());
  };

  // Move a single conversation to another column (called from MoveCopyToModal single-select path)
  const handleMoveFromColumn = (
    _e: any,
    conversation: ConversationDetail,
    newColumnId: number,
    response?: Dashboard,
  ) =>
  {
    setColumnTotals(prev => ({
      ...prev,
      [conversation.DashboardGroupId]: Math.max(0, (prev[conversation.DashboardGroupId] || 0) - 1),
      [newColumnId || 0]: (prev[newColumnId || 0] || 0) + 1,
    }));

    if (newColumnId)
    {
      setGroups(prev =>
        prev.map(group =>
        {
          if (group.DashboardGroupId === conversation.DashboardGroupId)
          {
            return {
              ...group,
              Conversations: group.Conversations.filter(
                c => c.ConversationId !== conversation.ConversationId,
              ),
            };
          }
          if (group.DashboardGroupId === newColumnId)
          {
            if (group.Conversations.some(c => c.ConversationId === conversation.ConversationId))
              return group;
            return {
              ...group,
              Conversations: [
                { ...conversation, DashboardGroupId: newColumnId },
                ...group.Conversations,
              ],
            };
          }
          return group;
        }),
      );
    }
    else if (response?.DashboardGroupId)
    {
      // Moved to a new column created by the API
      setGroups(prev =>
      {
        const filtered = prev.map(group =>
        {
          if (group.DashboardGroupId === conversation.DashboardGroupId)
          {
            return {
              ...group,
              Conversations: group.Conversations.filter(
                c => c.ConversationId !== conversation.ConversationId,
              ),
            };
          }
          return group;
        });
        if (!filtered.some(g => g.DashboardGroupId === response.DashboardGroupId))
        {
          const lastGroup = filtered[filtered.length - 1];
          const newCol = lastGroup ? lastGroup.col + lastGroup.sizeX : 0;
          const newGroup: DashboardGroupData = {
            DashboardId: dashboardId,
            DashboardGroupId: response.DashboardGroupId!,
            Conversations: [{ ...conversation, DashboardGroupId: response.DashboardGroupId! }],
            Name: 'New Group from Conversation',
            col: newCol,
            row: 0,
            sizeX: 3,
            sizeY: 0,
            isNew: true,
            TotalItems: 1,
            SocketConnectionId: socketConnectionId,
            IsDefault: false,
            showAllDetails: false,
            cancelDashboardNotification: false,
            UpdateGroup: null,
          };
          return [...filtered, newGroup];
        }
        return filtered;
      });
    }
  };

  // Update conversation handler
  const handleUpdateConversation = (
    conversationId: number,
    updates: Partial<ConversationDetail>,
  ) =>
  {
    setGroups(prev =>
      prev.map(group =>
      {
        // Apply the field update to the target conversation
        const updatedConvs = group.Conversations.map(conv =>
        {
          if (conv.ConversationId === conversationId)
          {
            return { ...conv, ...updates, reactSelfUpdate: true };
          }
          return conv;
        });

        // If Finished changed, recalculate Progress bottom-up across all hierarchy levels
        if ('Finished' in updates)
        {
          const targeted = updatedConvs.find(c => c.ConversationId === conversationId);
          if (targeted?.ParentConversationId)
          {
            const mutable = updatedConvs.map(c => ({ ...c }));
            let changed = true;
            while (changed)
            {
              changed = false;
              for (const conv of mutable)
              {
                if (!conv.IsParent) continue;
                const children = mutable.filter(c => c.ParentConversationId === conv.ConversationId);
                if (children.length === 0) continue;
                const avg = Math.round(
                  children.reduce((sum: number, c: any) => sum + (c.IsParent ? (c.Progress ?? 0) : (c.Finished ? 100 : 0)), 0) / children.length,
                );
                if (conv.Progress !== avg) { conv.Progress = avg; changed = true; }
              }
            }
            return { ...group, Conversations: mutable };
          }
        }

        return { ...group, Conversations: updatedConvs };
      }),
    );

    if ('IsRedFlag' in updates && updates.IsRedFlag !== undefined)
    {
      conversationBus.emit('redflag-updated', {
        conversationId,
        isRedFlag: updates.IsRedFlag as boolean,
      });
    }
    if ('IsImportant' in updates && updates.IsImportant !== undefined)
    {
      conversationBus.emit('important-updated', {
        conversationId,
        isImportant: updates.IsImportant as boolean | null,
      });
    }
    if ('Finished' in updates && updates.Finished !== undefined)
    {
      conversationBus.emit('completed-updated', {
        conversationId,
        isCompleted: updates.Finished as boolean,
      });
    }
  };

  const loadDataDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() =>
  {
    registerUpdateCustomFields((_fields, _conversation, updatedField) =>
    {
      if (updatedField?.Name === 'Status')
      {
        if (loadDataDebounceRef.current) clearTimeout(loadDataDebounceRef.current);
        loadDataDebounceRef.current = setTimeout(() =>
        {
          loadDataRef.current(false);
        }, 300);
      }
    });
  }, [registerUpdateCustomFields]);

  useEffect(() =>
  {
    const unsub1 = conversationBus.on('redflag-updated', ({ conversationId, isRedFlag }) =>
    {
      setGroups(prev =>
        prev.map(group => ({
          ...group,
          Conversations: group.Conversations.map(conv =>
            conv.ConversationId === conversationId ? { ...conv, IsRedFlag: isRedFlag } : conv,
          ),
        })),
      );
    });
    const unsub2 = conversationBus.on('important-updated', ({ conversationId, isImportant }) =>
    {
      setGroups(prev =>
        prev.map(group => ({
          ...group,
          Conversations: group.Conversations.map(conv =>
            conv.ConversationId === conversationId ? { ...conv, IsImportant: isImportant } : conv,
          ),
        })),
      );
    });
    const unsubCompleted = conversationBus.on('completed-updated', ({ conversationId, isCompleted }) =>
    {
      setGroups(prev =>
        prev.map(group =>
        {
          const updatedConvs = group.Conversations.map(conv =>
            conv.ConversationId === conversationId ? { ...conv, Finished: isCompleted, reactSelfUpdate: true } : { ...conv },
          );
          const targeted = updatedConvs.find(c => c.ConversationId === conversationId);
          if (!targeted?.ParentConversationId) return { ...group, Conversations: updatedConvs };

          // Recalculate progress bottom-up across all hierarchy levels
          let changed = true;
          while (changed)
          {
            changed = false;
            for (const conv of updatedConvs)
            {
              if (!conv.IsParent) continue;
              const children = updatedConvs.filter(c => c.ParentConversationId === conv.ConversationId);
              if (children.length === 0) continue;
              const avg = Math.round(
                children.reduce((sum: number, c: any) => sum + (c.IsParent ? (c.Progress ?? 0) : (c.Finished ? 100 : 0)), 0) / children.length,
              );
              if (conv.Progress !== avg) { conv.Progress = avg; changed = true; }
            }
          }
          return { ...group, Conversations: updatedConvs };
        }),
      );
    });
    const unsub3 = conversationBus.on('conversation-deleted', ({ conversationId }) =>
    {
      setGroups(prev =>
      {
        const ownerGroup = prev.find(g =>
          g.Conversations.some(c => c.ConversationId === conversationId),
        );
        if (ownerGroup && !ownerGroup.IsDefault)
        {
          setColumnTotals(t => ({
            ...t,
            [ownerGroup.DashboardGroupId]: Math.max(0, (t[ownerGroup.DashboardGroupId] || 0) - 1),
          }));
        }
        if (!ownerGroup) return prev;
        const idSet = new Set([conversationId]);
        return prev.map(group =>
        {
          if (!group.Conversations.some(conv => conv.ConversationId === conversationId)) return group;
          return {
            ...group,
            Conversations: removeConversationsAndFixParents(group.Conversations, idSet),
          };
        });
      });
    });
    const unsub4 = conversationBus.on(
      'conversation-moved-to-column',
      ({ conversationId, fromGroupId, toGroupId, conversation }) =>
      {
        // Use groupsRef to find the actual owner group — fromGroupId may be stale if
        // an external update (e.g. socket) changed DashboardGroupId after the modal opened.
        const ownerGroup = groupsRef.current.find(g =>
          g.Conversations.some(c => c.ConversationId === conversationId),
        );
        const actualFromGroupId = ownerGroup?.DashboardGroupId ?? fromGroupId;
        setColumnTotals(prev => ({
          ...prev,
          [actualFromGroupId]: Math.max(0, (prev[actualFromGroupId] || 0) - 1),
          [toGroupId || 0]: (prev[toGroupId || 0] || 0) + 1,
        }));
        if (toGroupId)
        {
          setGroups(prev =>
            prev.map(group =>
            {
              if (group.DashboardGroupId === actualFromGroupId)
              {
                return {
                  ...group,
                  Conversations: group.Conversations.filter(
                    c => c.ConversationId !== conversationId,
                  ),
                };
              }
              if (group.DashboardGroupId === toGroupId)
              {
                if (group.Conversations.some(c => c.ConversationId === conversationId))
                  return group;
                return {
                  ...group,
                  Conversations: [
                    { ...conversation, DashboardGroupId: toGroupId },
                    ...group.Conversations,
                  ],
                };
              }
              return group;
            }),
          );
        }
      },
    );
    const unsub5 = conversationBus.on(
      'conversation-added-to-column',
      ({ conversation, toGroupId }) =>
      {
        setColumnTotals(prev => ({
          ...prev,
          [toGroupId || 0]: (prev[toGroupId || 0] || 0) + 1,
        }));
        if (toGroupId)
        {
          setGroups(prev =>
            prev.map(group =>
            {
              if (group.DashboardGroupId !== toGroupId) return group;
              if (group.Conversations.some(c => c.ConversationId === conversation.ConversationId))
                return group;
              return {
                ...group,
                Conversations: [
                  { ...conversation, DashboardGroupId: toGroupId },
                  ...group.Conversations,
                ],
              };
            }),
          );
        }
      },
    );
    const unsubDuplicated = conversationBus.on(
      'conversation-duplicated-from-infowindow',
      ({ conversation, dashboardGroupId, socketConnectionId: socketId }) =>
      {
        if (!dashboardGroupId) return;

        setColumnTotals(prev => ({
          ...prev,
          [dashboardGroupId]: (prev[dashboardGroupId] || 0) + 1,
        }));
        setGroups(prev =>
          prev.map(group =>
          {
            if (group.DashboardGroupId !== dashboardGroupId) return group;
            if (group.Conversations.some(c => c.ConversationId === conversation.ConversationId))
              return group;
            return {
              ...group,
              Conversations: [{ ...conversation, DashboardGroupId: dashboardGroupId }, ...group.Conversations],
            };
          }),
        );

        if (dashboardIdRef.current)
        {
          dashboardGroupsService
            .insertDashboardGroupConversation({
              DashboardId: dashboardIdRef.current,
              DashboardGroupId: dashboardGroupId,
              ConversationId: conversation.ConversationId,
              ParentConversationId: 0,
              PreviousParentId: conversation.ConfigValueId ?? null,
              socketConnectionId: socketId,
            })
            .catch(err => console.error('Error inserting duplicated conversation into group:', err));
        }
      },
    );

    const unsubLayers = conversationBus.on('layers-updated', ({ conversationId, layers }) =>
    {
      setGroups(prev =>
        prev.map(group => ({
          ...group,
          Conversations: group.Conversations.map(conv =>
            conv.ConversationId === conversationId
              ? { ...conv, ConversationLayers: layers as any }
              : conv,
          ),
        })),
      );
    });

    return () =>
    {
      unsub1();
      unsub2();
      unsubCompleted();
      unsub3();
      unsub4();
      unsub5();
      unsubDuplicated();
      unsubLayers();
    };
  }, []);

  // Apply Progress updates from API responses (InsertDashboardGroupMultipleConversations, etc.)
  // Updates Progress for any conversation ID present in the response.
  const applyProgressUpdatesFromResponse = useCallback((response: any[]) =>
  {
    if (!response || response.length === 0) return;

    // Build progress map from response — include all returned conversations that have Progress
    const progressMap: Record<number, number> = {};
    for (const conv of response)
    {
      if (typeof conv.Progress === 'number' && typeof conv.ConversationId === 'number')
      {
        progressMap[conv.ConversationId] = conv.Progress;
      }
    }
    if (Object.keys(progressMap).length === 0) return;

    setGroups(prev =>
      prev.map(group => ({
        ...group,
        Conversations: group.Conversations.map(conv =>
        {
          if (conv.ConversationId in progressMap)
          {
            return { ...conv, Progress: progressMap[conv.ConversationId] as number };
          }
          return conv;
        }),
      })),
    );
  }, []);

  // Recalculates Progress for all parent cards within a given group based on how many
  // of their immediate children are marked Finished. Used after checkbox toggles.
  const recalcParentProgress = useCallback(
    (groupId: number, updatedConversationId: number, isFinished: boolean) =>
    {
      setGroups(prev =>
        prev.map(group =>
        {
          if (group.DashboardGroupId !== groupId) return group;

          const allConvs = group.Conversations.map(c =>
            c.ConversationId === updatedConversationId ? { ...c, Finished: isFinished } : { ...c },
          );

          // Recalculate progress bottom-up across all hierarchy levels
          let changed = true;
          while (changed)
          {
            changed = false;
            for (const conv of allConvs)
            {
              if (!conv.IsParent) continue;
              const children = allConvs.filter(c => c.ParentConversationId === conv.ConversationId);
              if (children.length === 0) continue;
              const avg = Math.round(
                children.reduce((sum: number, c: any) => sum + (c.IsParent ? (c.Progress ?? 0) : (c.Finished ? 100 : 0)), 0) / children.length,
              );
              if (conv.Progress !== avg) { conv.Progress = avg; changed = true; }
            }
          }

          return { ...group, Conversations: allConvs };
        }),
      );
    },
    [],
  );

  // Called when the checkbox (Finished) is toggled on a card.
  // 1. Applies any Progress values returned by the API response (server may recalculate).
  // 2. Also recalculates parents locally so the bar updates immediately without waiting for the API.
  const handleCallBackUpdateComplete = useCallback(
    (response: any) =>
    {
      const list: any[] = Array.isArray(response) ? response : [response];

      // Try to apply server-provided Progress (from UpdateCompleteDashboardGroupConversation response)
      applyProgressUpdatesFromResponse(list);

      // Additionally recalculate locally for each card in the response
      // (covers the case where the API response doesn't include parent Progress)
      for (const conv of list)
      {
        if (typeof conv.ConversationId === 'number' && typeof conv.DashboardGroupId === 'number')
        {
          recalcParentProgress(conv.DashboardGroupId, conv.ConversationId, conv.Finished ?? false);
        }
      }
    },
    [applyProgressUpdatesFromResponse, recalcParentProgress],
  );

  // Delete conversation handler
  const handleDeleteConversation = (conversationId: number) =>
  {
    const conversation = groups
      .flatMap(g => g.Conversations)
      .find(c => c.ConversationId === conversationId);
    const group = groups.find(g => g.DashboardGroupId === conversation?.DashboardGroupId);
    const idSet = new Set([conversationId]);

    setGroups(prev =>
      prev.map(group =>
      {
        if (!group.Conversations.some(conv => conv.ConversationId === conversationId)) return group;
        return {
          ...group,
          Conversations: removeConversationsAndFixParents(group.Conversations, idSet),
        };
      }),
    );
    // Child tasks are also shown as replies in the parent's Info Window
    conversationBus.emit('conversation-deleted', { conversationId });
    if (!group?.IsDefault && group)
    {
      setColumnTotals(prev => ({
        ...prev,
        [group.DashboardGroupId]: Math.max(0, (prev[group.DashboardGroupId] || 0) - 1),
      }));
    }
  };

  // Archive conversation handler — removes from UI state and decrements column total
  const handleArchiveConversation = (conversationId: number) =>
  {
    const group = groups.find(g => g.Conversations.some(c => c.ConversationId === conversationId));
    setGroups(prev =>
      prev.map(g => ({
        ...g,
        Conversations: g.Conversations.filter(c => c.ConversationId !== conversationId),
      })),
    );
    if (group && !group.IsDefault)
    {
      setColumnTotals(prev => ({
        ...prev,
        [group.DashboardGroupId]: Math.max(0, (prev[group.DashboardGroupId] || 0) - 1),
      }));
    }
  };

  // Bulk-delete conversations from UI state (removes them from all columns they appear in)
  const handleBulkDeleteConversations = (conversationIds: number[]) =>
  {
    const idSet = new Set(conversationIds);
    setGroups(prev =>
      prev.map(group =>
      {
        const removed = group.Conversations.filter(c => idSet.has(c.ConversationId)).length;
        if (removed === 0) return group;
        return {
          ...group,
          Conversations: removeConversationsAndFixParents(group.Conversations, idSet),
        };
      }),
    );
    setColumnTotals(prev =>
    {
      const next = { ...prev };
      groups.forEach(group =>
      {
        if (group.IsDefault) return;
        const removed = group.Conversations.filter(c => idSet.has(c.ConversationId)).length;
        if (removed > 0)
        {
          next[group.DashboardGroupId] = Math.max(0, (prev[group.DashboardGroupId] || 0) - removed);
        }
      });
      return next;
    });
  };

  // Add conversation handler
  const handleAddConversation = (conversation: ConversationDetail, dashboardGroupId: number) =>
  {
    const targetGroup = groupsRef.current.find(g => g.DashboardGroupId === dashboardGroupId);
    const alreadyExists = targetGroup?.Conversations.some(
      c => c.ConversationId === conversation.ConversationId,
    ) ?? false;

    if (targetGroup && !targetGroup.IsDefault && !alreadyExists)
    {
      setColumnTotals(t => ({ ...t, [dashboardGroupId]: (t[dashboardGroupId] || 0) + 1 }));
    }

    setGroups(prev =>
      prev.map((group: DashboardGroupData) =>
      {
        if (group.DashboardGroupId !== dashboardGroupId) return group;
        if (group.Conversations.some(c => c.ConversationId === conversation.ConversationId))
          return group;

        return {
          ...group,
          Conversations: [conversation, ...group.Conversations],
        };
      }),
    );
  };

  // Update Groups Position
  const onUpdateDashboardGroupPosition = useCallback(
    (removedDashboardGroupId: number) =>
    {
      let newGroups = groups.filter(g => g.DashboardGroupId != removedDashboardGroupId);
      newGroups = newGroups.sort(function (a, b)
      {
        return a.col - b.col;
      });

      for (let i = 0; i < newGroups.length; i++)
      {
        newGroups[i]!.col = i * 3;
        newGroups[i]!.row = 0;
        newGroups[i]!.sizeX = 3;
        newGroups[i]!.sizeY = 0;
      }

      dashboardGroupsService.updateDashboardGroupPosition(newGroups).then(function (_response)
      {
        setTimeout(function ()
        {
          setGroups(newGroups);
        }, 500);
      });
    },
    [groups],
  );

  // Make column dynamic (set as default) and reload conversations + totals
  const handleMakeDynamic = useCallback(
    async (dashboardGroupId: number, dashboardGroupDashboardId: number) =>
    {
      try
      {
        const updates = await dashboardGroupService.SetGroupAsDefault(
          dashboardGroupId,
          dashboardGroupDashboardId,
          socketConnectionId || '',
        );
        if (updates.length > 0)
        {
          setGroups(prev =>
            prev.map(g =>
            {
              const update = updates.find(u => u.dashboardGroupId === g.DashboardGroupId);
              return update ? { ...g, IsDefault: update.isDefault } : g;
            }),
          );
        }
        loadData(true);
      }
      catch (error)
      {
        console.error('Error making column dynamic:', error);
      }
    },
    [socketConnectionId, loadData],
  );

  // Infinite scroll: load next page of cards for a specific column
  const handleLoadMoreCards = useCallback(
    async (groupId: number) =>
    {
      if (columnLoadingMoreRef.current[groupId] || columnHasMoreRef.current[groupId] === false)
        return;

      const currentPage = columnPagesRef.current[groupId] ?? 1;
      const nextPage = currentPage + 1;

      columnPagesRef.current[groupId] = nextPage;
      setColumnLoadingMore(prev => ({ ...prev, [groupId]: true }));

      try
      {
        const bookmarksId = getBookmarksConfigValueId();
        const isBookmarks = configValueId === bookmarksId;
        const shouldSendConfigValueId = configValueId && configValueId !== 1;
        const resolvedConfigValueId = isBookmarks ? 0 : configValueId;

        const newConversations = onLoadMoreCards
          ? await onLoadMoreCards(groupId, nextPage)
          : await dashboardGroupsService.getMoreGroupConversations({
            CustomFields: extraFilterParams?.CustomFields || [],
            PageNumber: nextPage,
            PageSize: 100,
            DashboardGroupId: groupId,
            SearchText: searchText || '',
            LayersToHide: effectiveLayersToHide || '',
            ...(shouldSendConfigValueId && { ConfigValueId: resolvedConfigValueId }),
            ...(projectId && { Project: projectId }),
            // Same top-level filter params as the initial load (Item Type, Owner, etc.),
            // otherwise pages 2+ ignore every non-custom-field filter
            ...extraFilterParams,
          });

        if (!newConversations || newConversations.length === 0)
        {
          setColumnHasMore(prev => ({ ...prev, [groupId]: false }));
        }
        else
        {
          setGroups(prev =>
            prev.map(g =>
            {
              if (g.DashboardGroupId !== groupId) return g;
              const existingIds = new Set(g.Conversations.map(c => c.ConversationId));
              const unique = dedupeByConversationId(newConversations).filter(
                c => !existingIds.has(c.ConversationId),
              );
              return {
                ...g,
                Conversations: [...g.Conversations, ...unique] as ConversationDetail[],
              };
            }),
          );
          if (newConversations.length < 100)
          {
            setColumnHasMore(prev => ({ ...prev, [groupId]: false }));
          }
        }
      }
      catch (error)
      {
        console.error('Error loading more cards for group', groupId, error);
        columnPagesRef.current[groupId] = currentPage; // revert page on error
      }
      finally
      {
        setColumnLoadingMore(prev => ({ ...prev, [groupId]: false }));
      }
    },
    [searchText, effectiveLayersToHide, extraFilterParams, projectId, configValueId],
  );

  // Remove a conversation from groups (used by recycle bin restore/delete)
  const removeConversationFromGroups = useCallback((conversationId: number) =>
  {
    setGroups(prev =>
      prev.map(group => ({
        ...group,
        Conversations: group.Conversations.filter(c => c.ConversationId !== conversationId),
      })),
    );
  }, []);

  // Restore conversation from recycle bin
  const handleRestoreConversation = useCallback(
    async (conversationId: number) =>
    {
      const conv = groups
        .flatMap(g => g.Conversations)
        .find(c => c.ConversationId === conversationId);
      const configValueId = conv?.ConfigValueId;
      removeConversationFromGroups(conversationId);
      try
      {
        await conversationService.restoreConversationFromBin(conversationId);
        if (configValueId === 9)
        {
          window.dispatchEvent(new CustomEvent('sideMenuProjectsRefresh'));
        }
      }
      catch (error)
      {
        console.error('Error restoring conversation:', error);
      }
    },
    [groups, removeConversationFromGroups],
  );

  // Permanently delete conversation from recycle bin — shows confirmation modal first
  const handleDeleteConversationPermanently = useCallback(
    (conversationId: number) =>
    {
      const group = groups.find(g =>
        g.Conversations.some(c => c.ConversationId === conversationId),
      );
      setPendingDeleteConversationId(conversationId);
      setPendingDeleteGroupId(group?.DashboardGroupId ?? 0);
    },
    [groups],
  );

  const handleConfirmDeletePermanently = useCallback(async () =>
  {
    if (!pendingDeleteConversationId) return;
    const conversationId = pendingDeleteConversationId;
    const groupId = pendingDeleteGroupId;
    setPendingDeleteConversationId(null);
    setPendingDeleteGroupId(0);
    removeConversationFromGroups(conversationId);
    try
    {
      await conversationService.deleteConversationPermanently(conversationId, groupId);
    }
    catch (error)
    {
      console.error('Error deleting conversation permanently:', error);
    }
  }, [pendingDeleteConversationId, pendingDeleteGroupId, removeConversationFromGroups]);

  // --- Hierarchy Handlers ---

  // Toggle expand/collapse of a parent card
  const handleToggleExpandParent = useCallback(
    async (conversationId: number, groupId: number) =>
    {
      // Prevent concurrent fetches for the same conversationId
      if (expandingInFlight.current.has(conversationId)) return;

      // We use a promise to wrap the state update to safely determine isExpanding without adding expandedParentIds to deps
      const isExpanding = await new Promise<boolean>(resolve =>
      {
        setExpandedParentIds(prev =>
        {
          const next = new Set(prev);
          const expanding = !next.has(conversationId);
          if (expanding)
          {
            next.add(conversationId);
          }
          else
          {
            next.delete(conversationId);
          }
          resolve(expanding);
          return next;
        });
      });

      if (isExpanding)
      {
        expandingInFlight.current.add(conversationId);
        try
        {
          const hierarchyConvs = await dashboardGroupsService.getConversationHierarchy({
            ConversationId: conversationId,
            DashboardGroupId: groupId,
            DashboardId: dashboardId,
          });

          if (hierarchyConvs && hierarchyConvs.length > 0)
          {
            // Pre-compute new children using the ref so the count is available
            // synchronously (setGroups updater runs async, can't read from inside it)
            const currentGroup = groupsRef.current.find(
              g => g.DashboardGroupId === groupId,
            );
            const existingIds = new Set(
              currentGroup?.Conversations.map(c => c.ConversationId) ?? [],
            );
            const seenIds = new Set<number>();
            const newChildren = hierarchyConvs
              .map(c => ({
                ...c,
                ParentConversationId: c.ParentConversationId || conversationId,
              }))
              .filter(c =>
              {
                if (existingIds.has(c.ConversationId) || seenIds.has(c.ConversationId))
                  return false;
                seenIds.add(c.ConversationId);
                return true;
              });

            if (newChildren.length > 0)
            {
              setGroups(prevGroups =>
                prevGroups.map(group =>
                {
                  if (group.DashboardGroupId !== groupId) return group;
                  // Safety dedup in case of concurrent updates
                  const currentIds = new Set(group.Conversations.map(c => c.ConversationId));
                  const trulyNew = newChildren.filter(c => !currentIds.has(c.ConversationId));
                  if (trulyNew.length === 0) return group;
                  return {
                    ...group,
                    Conversations: [...group.Conversations, ...trulyNew],
                  };
                }),
              );

              setColumnTotals(prev => ({
                ...prev,
                [groupId]: (prev[groupId] || 0) + newChildren.length,
              }));
            }
          }
        }
        catch (err)
        {
          console.error('Error fetching conversation hierarchy:', err);
        }
        finally
        {
          expandingInFlight.current.delete(conversationId);
        }
      }
      else
      {
        // Collapsing: pre-compute descendants using the ref so the count is available
        // synchronously before calling setGroups
        const currentGroup = groupsRef.current.find(g => g.DashboardGroupId === groupId);
        if (currentGroup)
        {
          const toRemove = new Set<number>();
          const queue = [conversationId];
          while (queue.length > 0)
          {
            const pid = queue.shift()!;
            currentGroup.Conversations.forEach(c =>
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              if ((c as any).ParentConversationId === pid)
              {
                toRemove.add(c.ConversationId);
                queue.push(c.ConversationId);
              }
            });
          }

          if (toRemove.size > 0)
          {
            setGroups(prevGroups =>
              prevGroups.map(group =>
              {
                if (group.DashboardGroupId !== groupId) return group;
                return {
                  ...group,
                  Conversations: group.Conversations.filter(
                    c => !toRemove.has(c.ConversationId),
                  ),
                };
              }),
            );

            setColumnTotals(prev => ({
              ...prev,
              [groupId]: Math.max(0, (prev[groupId] || 0) - toRemove.size),
            }));
          }
        }
      }
    },
    [dashboardId],
  );

  // Handle react-nestable changes within expanded children section
  const handleNestableChange = useCallback(
    (params: {
      items: NestableItem[];
      dragItem: NestableItem;
      targetPath: number[];
      parentId: number;
      groupId: number;
    }) =>
    {
      const { items, parentId, groupId } = params;

      // Flatten the new tree back into ConversationDetail[]
      const flatChildren = flattenChildTree(items, parentId);

      // Optimistic update: replace children in the group
      setGroups(prev =>
        prev.map(group =>
        {
          if (group.DashboardGroupId !== groupId) return group;
          // Keep root cards + replace children under this parent
          const otherConvs = group.Conversations.filter(
            c => c.ParentConversationId !== parentId || c.ParentConversationId === 0,
          );
          return { ...group, Conversations: [...otherConvs, ...flatChildren] };
        }),
      );

      // API: update parent-child relationships
      // For each child, call insertDashboardGroupConversation
      for (const child of flatChildren)
      {
        dashboardGroupsService
          .insertDashboardGroupConversation({
            DashboardId: dashboardId,
            DashboardGroupId: groupId,
            ConversationId: child.ConversationId,
            ParentConversationId: child.ParentConversationId ?? 0,
            PreviousParentId: null,
            socketConnectionId: socketConnectionId || '',
          })
          .catch(err => console.error('Error updating child hierarchy:', err));
      }
    },
    [dashboardId, socketConnectionId],
  );

  // Handle "make child" — when a card is dropped on another card's child drop zone
  const handleMakeChild = useCallback(
    (
      childConversationId: number,
      parentConversationId: number,
      groupId: number,
      sourceGroupId?: number,
    ) =>
    {
      // Nest the whole selection, not just the dragged card, when multiple are selected
      const isMultiNest =
        selectedCardIds.has(childConversationId) &&
        selectedCardIds.size > 1 &&
        !selectedCardIds.has(parentConversationId);

      if (isMultiNest)
      {
        const childIds = new Set(selectedCardIds);

        setGroups(prev =>
        {
          const cardsToMove: ConversationDetail[] = [];
          for (const id of childIds)
          {
            for (const g of prev)
            {
              const card = g.Conversations.find(c => c.ConversationId === id);
              if (card)
              {
                cardsToMove.push(card);
                break;
              }
            }
          }
          if (cardsToMove.length === 0) return prev;

          const movedIds = new Set(cardsToMove.map(c => c.ConversationId));

          const newGroups = prev.map(group =>
          {
            if (group.DashboardGroupId === groupId)
            {
              const existing = group.Conversations.filter(c => !movedIds.has(c.ConversationId));
              const movedCards = cardsToMove.map(card => ({
                ...card,
                DashboardGroupId: groupId,
                ParentConversationId: parentConversationId,
              }));
              const conversations = [...existing, ...movedCards].map(c =>
                c.ConversationId === parentConversationId ? { ...c, IsParent: true } : c,
              );
              return { ...group, Conversations: recalcProgressForParents(conversations) };
            }
            // Remove moved cards from every other group + clean up former parents
            if (group.Conversations.some(c => movedIds.has(c.ConversationId)))
            {
              const filtered = group.Conversations.filter(c => !movedIds.has(c.ConversationId));
              const withParentFix = filtered.map(c =>
              {
                if (!c.IsParent) return c;
                const stillHasChildren = filtered.some(
                  x => x.ParentConversationId === c.ConversationId,
                );
                return stillHasChildren ? c : { ...c, IsParent: false };
              });
              return { ...group, Conversations: recalcProgressForParents(withParentFix) };
            }
            return group;
          });

          // Fire one API call per moved card
          for (const card of cardsToMove)
          {
            const oldParentId =
              card.ParentConversationId && card.ParentConversationId !== 0
                ? card.ParentConversationId
                : null;
            dashboardGroupsService
              .insertDashboardGroupConversation({
                DashboardId: dashboardId,
                DashboardGroupId: groupId,
                ConversationId: card.ConversationId,
                ParentConversationId: parentConversationId,
                PreviousParentId: oldParentId,
                socketConnectionId: socketConnectionId || '',
              })
              .catch(err => console.error('Error making child (multi):', err));
          }

          return newGroups;
        });

        setExpandedParentIds(prev =>
        {
          const next = new Set(prev);
          next.add(parentConversationId);
          return next;
        });

        if (sourceGroupId !== undefined && sourceGroupId !== groupId)
        {
          setColumnTotals(prev => ({
            ...prev,
            [sourceGroupId]: Math.max(0, (prev[sourceGroupId] || 0) - childIds.size),
            [groupId]: (prev[groupId] || 0) + childIds.size,
          }));
        }

        setSelectedCardIds(new Set());
        return;
      }

      const isCrossColumn = sourceGroupId !== undefined && sourceGroupId !== groupId;

      if (isCrossColumn && sourceGroupId !== undefined)
      {
        setColumnTotals(prev => ({
          ...prev,
          [sourceGroupId]: Math.max(0, (prev[sourceGroupId] || 0) - 1),
          [groupId]: (prev[groupId] || 0) + 1,
        }));
      }

      setGroups(prev =>
      {
        // Find the child card wherever it lives (source group)
        const sourceGroup = prev.find(g => g.DashboardGroupId === sourceGroupId);
        const childCard = sourceGroup?.Conversations.find(
          c => c.ConversationId === childConversationId,
        );
        if (!childCard) return prev;

        const isCrossColumn = sourceGroupId !== groupId;
        const oldParentId =
          childCard.ParentConversationId && childCard.ParentConversationId !== 0
            ? childCard.ParentConversationId
            : null;

        return prev.map(group =>
        {
          // Source group (cross-column only): remove the child card + clean old parent's IsParent
          if (isCrossColumn && group.DashboardGroupId === sourceGroupId)
          {
            const filtered = group.Conversations.filter(
              c => c.ConversationId !== childConversationId,
            ).map(c =>
            {
              if (oldParentId && c.ConversationId === oldParentId)
              {
                const stillHasChildren = group.Conversations.some(
                  x =>
                    x.ParentConversationId === oldParentId &&
                    x.ConversationId !== childConversationId,
                );
                return stillHasChildren ? c : { ...c, IsParent: false };
              }
              return c;
            });
            // Recalc Progress for old parent that may have lost a child
            return { ...group, Conversations: recalcProgressForParents(filtered) };
          }

          // Target group: update parent's IsParent + set/add child with new ParentConversationId
          if (group.DashboardGroupId === groupId)
          {
            let conversations = group.Conversations.map(c =>
            {
              if (!isCrossColumn && c.ConversationId === childConversationId)
                return { ...c, ParentConversationId: parentConversationId };
              if (c.ConversationId === parentConversationId) return { ...c, IsParent: true };
              // Same-column: clean old parent's IsParent if it has no remaining children
              if (
                !isCrossColumn &&
                oldParentId &&
                c.ConversationId === oldParentId &&
                oldParentId !== parentConversationId
              )
              {
                const stillHasChildren = group.Conversations.some(
                  x =>
                    x.ParentConversationId === oldParentId &&
                    x.ConversationId !== childConversationId,
                );
                return stillHasChildren ? c : { ...c, IsParent: false };
              }
              return c;
            });

            // Cross-column: append the card to the target group with updated groupId and ParentConversationId
            if (isCrossColumn)
            {
              conversations = [
                ...conversations,
                {
                  ...childCard,
                  DashboardGroupId: groupId,
                  ParentConversationId: parentConversationId,
                },
              ];
            }

            // Recalculate Progress for the new parent (and any parent in this group)
            return { ...group, Conversations: recalcProgressForParents(conversations) };
          }

          return group;
        });
      });

      // Auto-expand the parent
      setExpandedParentIds(prev =>
      {
        const next = new Set(prev);
        next.add(parentConversationId);
        return next;
      });

      // Fetch all existing children so siblings of the newly dropped card are shown
      dashboardGroupsService
        .getConversationHierarchy({
          ConversationId: parentConversationId,
          DashboardGroupId: groupId,
          DashboardId: dashboardId,
        })
        .then(hierarchyConvs =>
        {
          if (!hierarchyConvs || hierarchyConvs.length === 0) return;

          const currentGroup = groupsRef.current.find(g => g.DashboardGroupId === groupId);
          const existingIds = new Set(
            currentGroup?.Conversations.map(c => c.ConversationId) ?? [],
          );
          const seenIds = new Set<number>();
          const newChildren = hierarchyConvs
            .map(c => ({
              ...c,
              ParentConversationId: c.ParentConversationId || parentConversationId,
            }))
            .filter(c =>
            {
              if (existingIds.has(c.ConversationId) || seenIds.has(c.ConversationId))
                return false;
              seenIds.add(c.ConversationId);
              return true;
            });

          if (newChildren.length > 0)
          {
            setGroups(prevGroups =>
              prevGroups.map(group =>
              {
                if (group.DashboardGroupId !== groupId) return group;
                const currentIds = new Set(group.Conversations.map(c => c.ConversationId));
                const trulyNew = newChildren.filter(c => !currentIds.has(c.ConversationId));
                if (trulyNew.length === 0) return group;
                return { ...group, Conversations: [...group.Conversations, ...trulyNew] };
              }),
            );
            setColumnTotals(prev => ({
              ...prev,
              [groupId]: (prev[groupId] || 0) + newChildren.length,
            }));
          }
        })
        .catch(err => console.error('Error fetching siblings after make child:', err));

      // API call — use response to update Progress on parent cards live
      dashboardGroupsService
        .insertDashboardGroupConversation({
          DashboardId: dashboardId,
          DashboardGroupId: groupId,
          ConversationId: childConversationId,
          ParentConversationId: parentConversationId,
          PreviousParentId: null,
          socketConnectionId: socketConnectionId || '',
        })
        .catch(err => console.error('Error making child:', err));
    },
    [dashboardId, socketConnectionId, recalcProgressForParents, selectedCardIds],
  );

  void activeColumnId;

  // True when a selected card is being dragged with other selected cards
  const isMultiDragActive =
    activeCardId !== null && selectedCardIds.has(activeCardId) && selectedCardIds.size > 1;

  // ── Multi-drag cursor overlay ─────────────────────────────────────────────
  // A portal that follows the cursor during multi-card drag.
  // Position is updated via direct DOM manipulation (no state updates on dragover).
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() =>
  {
    const el = overlayRef.current;
    if (!el) return;

    if (!isMultiDragActive)
    {
      el.style.display = 'none';
      return;
    }

    // Build the overlay content once from the current selection titles
    const titles = getSelectedCardTitles();
    const MAX = 5;
    const visible = titles.slice(0, MAX);
    const remaining = titles.length - visible.length;

    el.innerHTML = '';
    visible.forEach(({ id, title, layerColor }, i) =>
    {
      // Row — matches .corgee_com_title-card-header-status-bar padding/layout
      const row = document.createElement('div');
      row.style.cssText = [
        'display:flex',
        'align-items:center',
        'padding:6px 14px 6px 0',
        'line-height:1',
        'overflow:hidden',
        'background:#ffffff',
        i < visible.length - 1 ? 'box-shadow:0 1px 0 #ddd' : '',
      ]
        .filter(Boolean)
        .join(';');

      // Layer indicator — matches .corgee_com_title-card-header-layers (9px wide)
      // First child is 4px wide (colored), rest are 1px thin strips
      const layers = document.createElement('div');
      layers.style.cssText =
        'display:inline-block;font-size:0;margin-right:4px;overflow:hidden;white-space:nowrap;width:9px;flex-shrink:0;';
      const layerBar = document.createElement('div');
      const barColor = layerColor || '#cccccc';
      layerBar.style.cssText = `display:inline-block;height:18px;margin:0 1px 0 0;width:4px;background:${barColor};`;
      const layerThin = document.createElement('div');
      layerThin.style.cssText = `display:inline-block;height:18px;margin:0;width:1px;background:${barColor};opacity:0.6;`;
      layers.appendChild(layerBar);
      layers.appendChild(layerThin);

      // Type icon area — matches .corgee_com_title-card-header-icons (16px icon)
      const iconWrap = document.createElement('div');
      iconWrap.style.cssText = 'display:inline-block;margin-right:4px;flex-shrink:0;';
      const iconEl = document.createElement('div');
      iconEl.style.cssText =
        'width:16px;height:16px;border-radius:2px;background:#9ca3af;opacity:0.7;';
      iconWrap.appendChild(iconEl);

      // Checkbox — small square
      const check = document.createElement('div');
      check.style.cssText =
        'width:14px;height:14px;border:1px solid #d1d5db;border-radius:2px;flex-shrink:0;background:#fff;margin-right:6px;';

      // Title text
      const span = document.createElement('span');
      span.style.cssText =
        'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;color:#414042;font-size:13px;font-weight:400;';
      span.textContent = title || `Card ${id}`;

      row.appendChild(layers);
      row.appendChild(iconWrap);
      row.appendChild(check);
      row.appendChild(span);
      el.appendChild(row);
    });
    if (remaining > 0)
    {
      const more = document.createElement('div');
      more.style.cssText =
        'height:22px;display:flex;align-items:center;justify-content:flex-end;padding:0 10px;font-size:11px;color:#6b7280;background:#f9fafb;border-top:1px solid #ddd;';
      more.textContent = `+${remaining} more`;
      el.appendChild(more);
    }

    el.style.display = 'block';

    let rafId = 0;
    const onDragOver = (e: DragEvent) =>
    {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() =>
      {
        el.style.transform = `translate(${e.clientX + 14}px, ${e.clientY + 6}px)`;
      });
    };

    document.addEventListener('dragover', onDragOver);
    return () =>
    {
      document.removeEventListener('dragover', onDragOver);
      cancelAnimationFrame(rafId);
      el.style.display = 'none';
    };
  }, [isMultiDragActive]);

  const isCompact = cardViewMode === 'compact';
  const colRowCounts = [5, 3, 4, 1];
  const showSkeleton = loading && !isBackgroundRefresh;

  return (
    <div className="dashboard-groups h-full">
      {showSkeleton ? (
        <div className="flex gap-3 overflow-x-auto pb-4 p-4 h-full min-h-0">
          {colRowCounts.map((rowCount, i) => (
            <div
              key={i}
              className="dg-skeleton-pulse flex-shrink-0"
              style={{ width: `${columnWidth}px`, animationDelay: `${i * 0.12}s` }}
            >
              <div className="dg-skeleton-header">
                <div className="dg-skeleton-header-bar" />
                <div className="dg-skeleton-header-count" />
              </div>
              <div className="dg-skeleton-col-body">
                {isCompact ? (
                  Array.from({ length: rowCount }).map((_, j) => (
                    <div key={j} className="dg-skeleton-row">
                      <div className="dg-skeleton-color-square" />
                      <div className="dg-skeleton-square" />
                      <div className="dg-skeleton-square-checkbox" />
                      <div
                        className="dg-skeleton-bar"
                        style={{ width: `${58 + ((j * 11 + i * 7) % 32)}%` }}
                      />
                    </div>
                  ))
                ) : (
                  <div className="dg-skeleton-card">
                    <div className="dg-skeleton-card-top">
                      <div className="dg-skeleton-square" style={{ width: 18, height: 18 }} />
                      <div className="dg-skeleton-square" style={{ width: 18, height: 18 }} />
                    </div>
                    <div className="dg-skeleton-card-lines">
                      <div className="dg-skeleton-bar" style={{ width: '75%', height: 13 }} />
                      <div className="dg-skeleton-bar" style={{ width: '55%', height: 10 }} />
                    </div>
                    <div className="dg-skeleton-card-field" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <p>No dashboard groups found</p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 relative flex flex-col">
          <div
            ref={scrollContainerRef}
            className="dashboard-groups-scroll-container flex gap-6 overflow-x-auto p-1 h-full min-h-0"
            onClick={handleContainerClick}
          >
            {groups.map((group, index) => (
              <DashboardGroupColumn
                key={group.DashboardGroupId}
                group={group}
                dashboardId={dashboardId}
                columnWidth={columnWidths[group.DashboardGroupId] || columnWidth}
                cardViewMode={cardViewMode}
                index={index}
                selectedCardIds={selectedCardIds}
                isMultiDragActive={isMultiDragActive}
                onCardCtrlClick={handleCardCtrlClick}
                onCardShiftClick={handleCardShiftClick}
                onColumnNameChange={handleColumnNameChange}
                onAddNewTask={handleAddNewTask}
                isAddingTask={group.DashboardGroupId === addingTaskGroupId}
                onCancelAddTask={handleCancelAddTask}
                onTaskAdded={handleTaskAdded}
                configValueId={configValueId}
                projectId={projectId}
                projectName={projectName}
                api={api}
                config={config}
                storageData={getStorageData}
                realItemTypes={realItemTypes}
                configMenus={
                  recycleBin
                    ? { conversation: ['close-infowindow', 'restore', 'delete-permanently'] }
                    : getConfigMenus()
                }
                userData={getUserData()}
                onOpenInfoWindow={onOpenInfoWindow}
                onEditConversation={handleEditConversation}
                onCreateConversation={handleCreateConversation}
                onUpdateConversation={handleUpdateConversation}
                onDeleteConversation={handleDeleteConversation}
                onArchiveConversation={handleArchiveConversation}
                onRestoreConversation={recycleBin ? handleRestoreConversation : undefined}
                onDeleteConversationPermanently={
                  recycleBin ? handleDeleteConversationPermanently : undefined
                }
                onDuplicateItem={handleDuplicateConversation}
                onRemoveFromColumn={handleRemoveFromColumn}
                onBulkRemoveFromColumn={handleBulkRemoveFromColumn}
                onBulkDeleteConversations={handleBulkDeleteConversations}
                onConversationsProjectChanged={handleConversationsProjectChanged}
                onChangedProject={handleConversationProjectChanged}
                onMoveFromColumn={handleMoveFromColumn}
                onCreateItemFromCustomType={handleCreateItemFromCustomType}
                onAddToColumn={handleAddConversation}
                recycleBin={recycleBin}
                hasArchivedFilter={extraFilterParams?.Archived !== undefined}
                totalItems={columnTotals[group.DashboardGroupId]}
                hideProjectName={hideProjectName}
                pendingGroupIds={pendingGroupIds}
                pendingConversationIds={pendingConversationIds}
                onUpdateDashboardGroupPosition={onUpdateDashboardGroupPosition}
                onMakeDynamic={handleMakeDynamic}
                onLoadMoreCards={handleLoadMoreCards}
                hasMoreCards={columnHasMore[group.DashboardGroupId] !== false}
                isLoadingMore={columnLoadingMore[group.DashboardGroupId] === true}
                expandedParentIds={expandedParentIds}
                onToggleExpandParent={handleToggleExpandParent}
                onNestableChange={handleNestableChange}
                onMakeChild={handleMakeChild}
                hierarchyDropActive={hierarchyDropActive}
                onCardDrop={handleCardDropped}
                onColumnDrop={handleColumnDropped}
                handleSelectAllCards={handleSelectAllCards}
                getSelectedConversations={getSelectedConversations}
                onCallBackUpdateComplete={handleCallBackUpdateComplete}
                onInfoWindowCardDrop={handleInfoWindowCardDrop}
                socketConnectionId={socketConnectionId}
              />
            ))}
            {/* Add column button — hidden in recycle bin mode */}
            {!recycleBin && (
              <div className="flex-shrink-0 flex items-center dashboard-groups-add-column-wrapper">
                <button
                  onClick={handleAddColumn}
                  className="dashboard-groups-add-column enable-help-tooltip"
                  ht-name="Add column"
                />
              </div>
            )}
          </div>
          <div
            ref={fakeScrollRef}
            className="dashboard-groups-fake-scrollbar"
          >
            <div style={{ width: scrollWidth, height: '1px' }} />
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingDeleteConversationId !== null}
        onClose={() =>
        {
          setPendingDeleteConversationId(null);
          setPendingDeleteGroupId(0);
        }}
        onConfirm={handleConfirmDeletePermanently}
        title="Delete Item Permanently"
        description="Are you sure you want to delete this item Permanently?"
        confirmText="Yes"
        cancelText="No"
        confirmVariant="destructive"
      />

      <EditCard
        ref={editCardRef}
        config={{
          apiUrl: api.url,
          authToken: api.token,
          SocketConnectionId: socketConnectionId,
          attachReactFunctions: (fns: any) =>
          {
            editCardInitRef.current = fns.initConfig;
          },
          functions: {
            loadingBarStart: () => {},
            loadingBarComplete: () => {},
            conversationResponse: (_payload: any, data: any) =>
            {
              const responseConvId =
                  data?.ConversationId ?? data?.Conversation?.ConversationId;
              if (!responseConvId) return;

              const sourceConv = createSourceConvRef.current;

              if (!sourceConv)
              {
                // Edit or create from blue button — reload everything
                loadData(true);
                return;
              }

              // "Create from card" action menu — targeted update
              createSourceConvRef.current = null;

              const newConvData = data?.Conversation ?? data;
              const targetGroupId =
                  newConvData.DashboardGroupId ||
                  (sourceConv as any).DashboardGroupId ||
                  groupsRef.current.find((g: any) =>
                    g.Conversations.some(
                      (c: any) => c.ConversationId === sourceConv.ConversationId,
                    ),
                  )?.DashboardGroupId;
              const sourceGroup = groupsRef.current.find(
                (g: any) => g.DashboardGroupId === targetGroupId,
              );

              if (!sourceGroup)
              {
                loadData(true);
                return;
              }

              const baseConv = {
                ...newConvData,
                DashboardGroupId: targetGroupId,
                DashboardId: newConvData.DashboardId || (sourceConv as any).DashboardId,
              };

              if (sourceGroup.IsDefault)
              {
                // Dynamic column: add as standalone card in the default column, no parent nesting
                handleAddConversation(baseConv, targetGroupId);
              }
              else
              {
                // Normal column: insert as child and mark source as IsParent
                const newConv = { ...baseConv, ParentConversationId: sourceConv.ConversationId };
                setGroups(prev =>
                  prev.map(group =>
                  {
                    if (group.DashboardGroupId !== targetGroupId) return group;

                    const withNewConv = [
                      ...group.Conversations.map((c: any) =>
                        c.ConversationId === sourceConv.ConversationId
                          ? { ...c, IsParent: true }
                          : { ...c },
                      ),
                      { ...newConv },
                    ];

                    // Recalculate progress bottom-up so all ancestors reflect the new child.
                    // If sourceConv was already a parent but its prior children aren't loaded,
                    // skip its recalc to preserve the existing Progress value — the relatedCardCreated
                    // bus event will fetch the accurate value from the server and cascade correctly.
                    const sourceConvWasParent = (sourceConv as any).IsParent === true;
                    const sourceConvHasLoadedChildren = withNewConv.some(
                      (c: any) =>
                        c.ParentConversationId === sourceConv.ConversationId &&
                          c.ConversationId !== newConv.ConversationId,
                    );
                    const skipSourceConvRecalc = sourceConvWasParent && !sourceConvHasLoadedChildren;

                    let changed = true;
                    while (changed)
                    {
                      changed = false;
                      for (const conv of withNewConv)
                      {
                        if (!conv.IsParent) continue;
                        if (skipSourceConvRecalc && conv.ConversationId === sourceConv.ConversationId) continue;
                        const children = withNewConv.filter((c: any) => c.ParentConversationId === conv.ConversationId);
                        if (children.length === 0) continue;
                        const avg = Math.round(
                          children.reduce((sum: number, c: any) => sum + (c.IsParent ? (c.Progress ?? 0) : (c.Finished ? 100 : 0)), 0) / children.length,
                        );
                        if (conv.Progress !== avg) { conv.Progress = avg; changed = true; }
                      }
                    }

                    return { ...group, Conversations: withNewConv };
                  }),
                );
                // Expand source conv so newConv is visible in the nested tree
                setExpandedParentIds(prev =>
                {
                  const next = new Set(prev);
                  next.add(sourceConv.ConversationId);
                  return next;
                });
              }
            },
          },
          params: { Conversation: {} },
        }}
        UpdateCustomFields={() => {}}
      />
      {/* DuplicateCard */}
      <DuplicateCard
        ref={duplicateCardRef}
        config={{
          apiUrl: api.url,
          authToken: api.token,
          SocketConnectionId: socketConnectionId,
          attachReactFunctions: (fns: any) =>
          {
            duplicateCardInitRef.current = fns.initConfig;
          },
          DashboardId: dashboardId,
          functions: {
            loadingBarStart: () => {},
            loadingBarComplete: () => {},
            conversationResponse: (_payload: any, data: any) =>
            {
              if (data?.ConversationId)
              {
                // To keep it simple, background refresh everything when a card is edited
                loadData(true);
              }
            },
            addConversation(conversation, dashboardGroupId)
            {
              handleAddConversation(conversation, dashboardGroupId);
            },
          },
          params: { Conversation: {} },
        }}
        UpdateCustomFields={() => {}}
      />

      {/* Multi-drag cursor overlay — content & position managed via DOM refs, no re-renders on dragover */}
      {createPortal(
        <div
          ref={overlayRef}
          style={{
            display: 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            pointerEvents: 'none',
            zIndex: 99999,
            willChange: 'transform',
            background: '#ffffff',
            borderRadius: '1px',
            border: '1px solid #ddd',
            boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            minWidth: '220px',
            maxWidth: '360px',
            fontSize: '13px',
            fontFamily: 'inherit',
            color: '#414042',
            opacity: 0.92,
          }}
        />,
        document.body,
      )}
    </div>
  );
};

export default DashboardGroups;
